// src/components/Layout/Layout.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import styles from './Layout.module.css';

// Component Imports
import Sidebar from './Sidebar';
import StickyHeader from './StickyHeader';
import CharacterAndInventoryView from '../CharacterAndInventoryView';
import HomeView from '../HomeView';
import WildernessView from '../WildernessView';
import ShopView from '../ShopView';
import MainPanel2 from './MainPanel2';

import CollapsibleInventoryPanel from './CollapsibleInventoryPanel';
import QuestsPanel from './QuestsPanel';
import CombatView from '../CombatView';
import FishingMinigame from '../FishingMinigame';
import MiningMinigame from '../MiningMinigame';
import ForagingMinigame from '../ForagingMinigame';
import WoodChoppingMinigame from '../WoodChoppingMinigame';
import DungeonMiningMinigame from '../DungeonMiningMinigame';
import FarmingMinigame from '../FarmingMinigame';
import SkillsPanel from '../SkillsPanel';
import EndOfDayModal from '../EndOfDayModal/EndOfDayModal';
import AchievementsPanel from './AchievementsPanel';
import AchievementModal from '../AchievementModal/AchievementModal';

// Building View Imports
import FurnaceView from '../BuildingViews/FurnaceView';
import ForgeView from '../BuildingViews/ForgeView';
import SawmillView from '../BuildingViews/SawmillView';
import WorkbenchView from '../BuildingViews/WorkbenchView';
import AnvilView from '../BuildingViews/AnvilView';
import CampfireView from '../BuildingViews/CampfireView';
import BrewingStationView from '../BuildingViews/BrewingStationView';
import TanningStationView from '../BuildingViews/TanningStationView';
import RunecraftingStationView from '../BuildingViews/RunecraftingStationView';
import LandPlotView from '../BuildingViews/LandPlotView';
import FletchingStationView from '../BuildingViews/FletchingStationView';
import ThievingDummyView from '../BuildingViews/ThievingDummyView';
import CollapsibleAdminPanel from './CollapsibleAdminPanel';

// Data Imports
import { ItemData } from '../../data/ItemData';
import { gameConfigData, skillData as allSkillDefinitions, defaultPlayerStats } from '../../data/data';
import { questDefinitions } from '../../data/questData';
import { customerOrders as customerOrderDefinitions } from '../../data/customerOrderData';
import { MonsterData } from '../../data/monsterData';

// Achievement Utility Imports
import { 
    updatePlayerStat, 
    updateCombatStats, 
    checkForNewAchievements 
} from '../../utils/achievementUtils';

// Stat Progression Utility Imports
import { calculateTotalPlayerStats } from '../../utils/statProgressionUtils';

// Context Import
import { MessageLogContext } from '../../contexts/MessageLogContext';
import { AuthContext } from '../../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const WAKE_UP_HOUR = 4;
const MAX_DAY_MINUTES = (24 - WAKE_UP_HOUR + 1) * 60;
const MINUTES_PER_TICK = gameConfigData.MINUTES_PER_CONTINUOUS_TICK || 10;
const GAME_TICK_INTERVAL_MS = 1000;
const DURABILITY_LOSS_CHANCE = gameConfigData.DURABILITY_LOSS_CHANCE || 0.3;
const MAX_BUY_BACK_SLOTS = 10;
const BOSS_WAVE_COUNT = 3;

const gameActionsConfig = gameConfigData.actions;

const formatGameTime = (currentMinutesInDay, wakeUpHour = WAKE_UP_HOUR) => {
    const totalMinutesFromMidnight = (wakeUpHour * 60) + Math.floor(currentMinutesInDay + 0.00001);
    let hour = Math.floor(totalMinutesFromMidnight / 60) % 24;
    const minute = totalMinutesFromMidnight % 60;
    const period = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    if (hour === 0) { hour = 12; }
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
};



let nextItemIdCounter = 1;
const generateItemUniqueId = (baseKey) => `item_${baseKey}_${Date.now()}_${nextItemIdCounter++}`;
let nextQuestInstanceId = 1;
const generateQuestInstanceId = () => `qi_${Date.now()}_${nextQuestInstanceId++}`;
let nextOrderInstanceId = 1;
const generateOrderInstanceId = () => `coi_${Date.now()}_${nextOrderInstanceId++}`;


const createInitialItemInstance = (itemKey) => {
    const baseItem = ItemData[itemKey];
    if (!baseItem) {
        console.error(`[Layout] Base item data not found for key: ${itemKey}`);
        return null;
    }
    const id = generateItemUniqueId(itemKey);
    return {
        id, key: itemKey, name: baseItem.name, type: baseItem.type,
        toolType: baseItem.toolType !== undefined ? baseItem.toolType : null,
        weaponType: baseItem.weaponType !== undefined ? baseItem.weaponType : null,
        slotType: baseItem.slotType !== undefined ? baseItem.slotType : null,
        handedness: baseItem.handedness !== undefined ? baseItem.handedness : null,
        damage: baseItem.damage !== undefined ? baseItem.damage : null,
        durability: baseItem.maxDurability !== undefined ? baseItem.maxDurability : null,
        maxDurability: baseItem.maxDurability !== undefined ? baseItem.maxDurability : null,
        weight: baseItem.weight !== undefined ? baseItem.weight : 0,
        goldValue: baseItem.goldValue !== undefined ? baseItem.goldValue : 0,
        img: baseItem.img !== undefined ? baseItem.img : '',
        description: baseItem.description !== undefined ? baseItem.description : '',
        effects: baseItem.effects !== undefined ? baseItem.effects : null,
        stackable: baseItem.stackable !== undefined ? baseItem.stackable : false,
        materials: baseItem.materials !== undefined ? baseItem.materials : null,
        shopCategory: baseItem.shopCategory !== undefined ? baseItem.shopCategory : null,
        shopPriceOverride: baseItem.shopPriceOverride !== undefined ? baseItem.shopPriceOverride : null,
        category: baseItem.category !== undefined ? baseItem.category : null,
    };
};

const createDefaultPlayerData = (characterName = "Hero") => {
  const initialInventory = {};
  // New starting items: 5 berries and 1 shiny pebble
  initialInventory['berries'] = 5;
  initialInventory['shinyPebble'] = 1;

  const initialSkills = {};
  for (const skillKey in allSkillDefinitions) {
      initialSkills[skillKey] = { level: 1, xp: 0 };
  }

  return {
      name: characterName,
      id: `char_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, 
      health: { current: 100, max: 100 },
      mana: { current: 75, max: 75 },
      energy: { current: 100, max: 100 },
      unlockedTier: 1,
      gold: 50,
      currentLocation: 'home',
      tripInventory: {},
      currentCarryWeight: 0,
      unlockedSeeds: [], // Array of seed keys that have been unlocked for foraging
      equipment: {
          mainHand: null,
          offHand: null, helmet: null,
          chest: null, pants: null, gloves: null, boots: null, belt: null,
          necklace: null, ring1: null, ring2: null, back: null,
      },
      inventory: initialInventory,
      lockedItems: {}, // Track which items are locked from selling
      skills: initialSkills,
              stats: {
          strength: 10, dexterity: 10, intelligence: 10, constitution: 10, luck: 0, defense: 0,
          health: { current: 100, max: 100 }, damageMin: 1, damageMax: 2,
        },
      carryCapacity: 100, 
      buildings: {
          furnace: { owned: false, upgradeLevel: 0 }, forge: { owned: false, upgradeLevel: 0 },
          sawmill: { owned: false, upgradeLevel: 0 }, workbench: { owned: false, upgradeLevel: 0 },
          shopFront: { owned: false, upgradeLevel: 0 }, questBoard: { owned: false, upgradeLevel: 0 },
          anvil: { owned: false, upgradeLevel: 0 },
          campfire: { owned: false, upgradeLevel: 0 }, brewingStation: { owned: false, upgradeLevel: 0 },
          tanningStation: { owned: false, upgradeLevel: 0 }, runecraftingStation: { owned: false, upgradeLevel: 0 },
          landPlot: { owned: false, upgradeLevel: 0 }, fletchingStation: { owned: false, upgradeLevel: 0 },
        dungeonEntrance: { owned: false, upgradeLevel: 0 },
          thievingDummy: { owned: false, upgradeLevel: 0 },
      },
      activeQuests: [],
      customerOrders: [],
      day: 1,
      monsterKills: {}, // Track individual monster kills for progression system
      playerStats: defaultPlayerStats,
      completedAchievements: [],
      dailyQuestOrderManagement: {
          questsPurchased: 0,
          ordersPurchased: 0,
          lastResetDay: 1
      },
      dailyConsumablePurchases: {
          healingPotion: 0,
          energyPotion: 0,
          manaPotion: 0,
          bandage: 0,
          apple: 0,
          berries: 0,
          lastResetDay: 1
      },
      timeOfDay: 'morning',
      gameTime: { hour: 8, minute: 0 },
      selectedDifficulty: 'medium',
      worldState: { isPaused: false, gameSpeed: 1 },
      settings: { autoSave: true, notifications: true },
      unlockedLocations: ['home', 'wilderness'],
      lastSaveTime: Date.now(),
      createdAt: Date.now(),
      version: '1.0.0'
  };
};


const getEnemyCountForRegularEncounter = () => {
    const roll = Math.random() * 100;
    if (roll < 60) return 1;
    if (roll < 80) return 2;
    if (roll < 90) return 3;
    if (roll < 97) return 4;
    return 5;
};

// Helper function to calculate level from XP (reverse of getXpToNextLevel formula)
const calculateLevelFromXP = (xp) => {
  if (xp <= 0) return 1;
  
  // Formula: xpForLevel = (level * 100) + 100
  // We need to solve for level given XP
  // For level 1: needs 200 XP (1 * 100 + 100)
  // For level 2: needs 300 XP (2 * 100 + 100)
  // For level 3: needs 400 XP (3 * 100 + 100)
  // etc.
  
  let level = 1;
  let totalXPNeeded = 0;
  
  while (totalXPNeeded < xp) {
    totalXPNeeded += (level * 100) + 100;
    if (totalXPNeeded <= xp) {
      level++;
    }
  }
  
  return level;
};

// Helper function to update skill XP and recalculate level
const updateSkillXP = (skill, xpGain) => {
  const newXP = (skill.xp || 0) + xpGain;
  const newLevel = calculateLevelFromXP(newXP);
  return { ...skill, xp: newXP, level: newLevel };
};

// Helper function to map monster keys to quest targets
const getMonsterKillCount = (defeatedMonsterKeys, questTarget) => {
  const monsterMapping = {
    'slimes': ['slime'],
    'goblins': [
      'goblinBasic', 'goblinWarrior', 'armoredGoblin', 'hobgoblinLeader', 
      'hobgoblinGrunt', 'hobgoblinArcher', 'hobgoblinEnforcer', 'goblinForager',
      'caveGoblinThug'
    ],
    'spiders': ['giantSpider', 'giantForestSpider'],
    'wolves': ['wolfPup', 'gloomfangWolfPup', 'krellGloomfangAlpha'],
    'bears': ['caveBear'],
    'bats': ['forestBat', 'deepLurkerBat'],
    'boars': ['wildBoar'],
    'beetles': ['caveBeetle'],
    'golems': ['smallRockGolem', 'mediumIronGolem', 'lootGolem', 'ancientGolem', 'rockGolem', 'ironGolem'],
    'hobgoblins': ['hobgoblinLeader', 'hobgoblinGrunt', 'hobgoblinArcher', 'hobgoblinEnforcer'],
    'bandits': ['banditThug', 'banditRogue', 'gloomwoodBanditScout'],
    'elementals': ['earthElemental'],
    'ghosts': ['ghost', 'specter'],
    'undead': ['ghost', 'specter'], // Alternative mapping for undead
    'werewolves': ['corruptedWerewolf'],
    'dragons': ['dragonkinWhelp'],
    'crabs': ['giantCaveCrab'],
    'salamanders': ['murkwaterSalamander']
  };
  
  const targetMonsterKeys = monsterMapping[questTarget] || [];
  if (targetMonsterKeys.length === 0) {
    // If no mapping found, try direct key match
    return defeatedMonsterKeys.includes(questTarget) ? 1 : 0;
  }
  
  return defeatedMonsterKeys.filter(key => targetMonsterKeys.includes(key)).length;
};

// Helper function to get display name for monster types in quest messages
const getMonsterTypeDisplayName = (questTitle) => {
  const titleMapping = {
    'Slime Menace': 'Slimes',
    'Goblin Scouts': 'Goblins',
    'Spider Threat': 'Spiders',
    'Wolf Pack': 'Wolves',
    'Bear Hunt': 'Bears',
    'Bandit Problem': 'Bandits',
    'Undead Menace': 'Undead',
    'Golem Destroyer': 'Golems',
    'Elemental Crisis': 'Elementals',
    'Dragon Threat': 'Dragons'
  };
  
  return titleMapping[questTitle] || 'Monsters';
};

const Layout = ({ initialPlayerData, isGuestPlayer, onReturnToDashboard }) => {
  const { addMessage } = useContext(MessageLogContext);
  const { currentUser } = useContext(AuthContext); 

  const [activeTab, setActiveTab] = useState('overview');
  const [buyBackList, setBuyBackList] = useState([]);
  const [isCombatActive, setIsCombatActive] = useState(false);
  const [currentCombatantPlayer, setCurrentCombatantPlayer] = useState(null);
  const [currentCombatantMonsters, setCurrentCombatantMonsters] = useState([]);
  const [isFishingMinigameActive, setIsFishingMinigameActive] = useState(false);
  const [isMiningMinigameActive, setIsMiningMinigameActive] = useState(false);
  const [isForagingMinigameActive, setIsForagingMinigameActive] = useState(false);
  const [isWoodChoppingMinigameActive, setIsWoodChoppingMinigameActive] = useState(false);
  const [isDungeonMiningMinigameActive, setIsDungeonMiningMinigameActive] = useState(false);
  const [isFarmingMinigameActive, setIsFarmingMinigameActive] = useState(false);
  const [bossEncounterQueue, setBossEncounterQueue] = useState([]);
  const [currentBossEncounterTier, setCurrentBossEncounterTier] = useState(null);
  const [isBossWaveActive, setIsBossWaveActive] = useState(false);
  const [currentWaveNumber, setCurrentWaveNumber] = useState(0);

  const [showEndOfDayModal, setShowEndOfDayModal] = useState(false);
  const [endOfDayData, setEndOfDayData] = useState({ dayNumber: 0, summary: {}, isPassingOut: false });
  const [isProcessingEndOfDay, setIsProcessingEndOfDay] = useState(false);
  
  // Achievement Modal State
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [achievementQueue, setAchievementQueue] = useState([]);
  
  // Mobile Menu State
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  
  // Settings State
  const [isAdminEnabled, setIsAdminEnabled] = useState(() => {
    const saved = localStorage.getItem('adminPanelEnabled');
    return saved ? JSON.parse(saved) : false;
  });

  const [tooltipsEnabled, setTooltipsEnabled] = useState(() => {
    const saved = localStorage.getItem('tooltipsEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default to true
  });

  const [showSettingsModal, setShowSettingsModal] = useState(false);


  const [playerData, setPlayerData] = useState(() => {
    let dataToUse = initialPlayerData;
    if (isGuestPlayer) {
      const guestSave = localStorage.getItem('playerData_guest');
      if (guestSave) {
        try { dataToUse = JSON.parse(guestSave); } 
        catch (e) { 
            console.error("[Layout] Error parsing guest save", e); 
            localStorage.removeItem('playerData_guest'); 
            dataToUse = createDefaultPlayerData("Guest Player");
        }
      } else { dataToUse = createDefaultPlayerData("Guest Player"); }
    } else if (!initialPlayerData) {
      dataToUse = createDefaultPlayerData("Hero");
    }
    
    // Ensure monsterKills property exists for existing players
    if (dataToUse && !dataToUse.monsterKills) {
      dataToUse.monsterKills = {};
    }
    
    return dataToUse;
  });

  const [worldState, setWorldState] = useState({
    day: playerData?.day || 1,
    currentTimeInMinutes: playerData?.currentTimeInMinutes || 0,
    isPaused: false, 
    _passedOut: false,
  });
  
  useEffect(() => {
    if (initialPlayerData && initialPlayerData.id) { 
      // Ensure monsterKills property exists for existing players
      if (!initialPlayerData.monsterKills) {
        initialPlayerData.monsterKills = {};
      }
      
      setPlayerData(initialPlayerData);
      setWorldState({
        day: initialPlayerData.day || 1,
        currentTimeInMinutes: initialPlayerData.currentTimeInMinutes || 0,
        isPaused: false, 
        _passedOut: false,
      });
      console.log("[Layout] Synced with initialPlayerData (ID:", initialPlayerData.id, "):", initialPlayerData);
    } else if (isGuestPlayer && initialPlayerData) { // For guest initial load
        // Ensure monsterKills property exists for existing players
        if (!initialPlayerData.monsterKills) {
          initialPlayerData.monsterKills = {};
        }
        
        setPlayerData(initialPlayerData);
         setWorldState({
            day: initialPlayerData.day || 1,
            currentTimeInMinutes: initialPlayerData.currentTimeInMinutes || 0,
            isPaused: false, _passedOut: false,
        });
    }
  }, [initialPlayerData, isGuestPlayer]);


  // Debounce mechanism to prevent rapid saves
  const lastSaveTimeRef = useRef(0);
  const saveDebounceMs = 5000; // Minimum 5 seconds between saves
  
  // Only save on day advancement - remove frequent autosave to prevent crashes
  const saveGameData = useCallback(async (forceImmediate = false) => {
    const now = Date.now();
    
    // Prevent rapid saves unless forced
    if (!forceImmediate && now - lastSaveTimeRef.current < saveDebounceMs) {
      console.log(`[Layout] Save skipped - too soon after last save (${now - lastSaveTimeRef.current}ms ago)`);
      return;
    }
    
    lastSaveTimeRef.current = now;
    
    if (currentUser && !isGuestPlayer && playerData && playerData.id) {
      try {
        console.log(`[Layout] Starting Firebase save for character ${playerData.name} (ID: ${playerData.id})`);
        const charDocRef = doc(db, "users", currentUser.uid, "characters", playerData.id);
        const dataToSave = { 
          ...playerData, 
          day: worldState.day, 
          currentTimeInMinutes: worldState.currentTimeInMinutes 
        };
        await setDoc(charDocRef, dataToSave, { merge: true });
        console.log(`[Layout] ✅ Successfully saved character ${playerData.name} (ID: ${playerData.id}) - Day ${worldState.day}, Time ${worldState.currentTimeInMinutes}.`);
      } catch (error) {
        console.error("[Layout] ❌ Error saving character to Firebase:", error);
      }
    }
    
    if (isGuestPlayer && playerData) {
      localStorage.setItem('playerData_guest', JSON.stringify({ ...playerData, day: worldState.day, currentTimeInMinutes: worldState.currentTimeInMinutes }));
      console.log(`[Layout] Guest game data autosaved to localStorage (Day ${worldState.day}).`);
    }
  }, [currentUser, isGuestPlayer, playerData, worldState]);

  // Force save on component unmount to prevent data loss during normal exit
  // Use refs to avoid recreating cleanup function on every state change
  const currentUserRef = useRef(currentUser);
  const isGuestPlayerRef = useRef(isGuestPlayer);
  const playerDataRef = useRef(playerData);
  const worldStateRef = useRef(worldState);
  
  // Update refs when values change
  useEffect(() => {
    currentUserRef.current = currentUser;
    isGuestPlayerRef.current = isGuestPlayer;
    playerDataRef.current = playerData;
    worldStateRef.current = worldState;
  }, [currentUser, isGuestPlayer, playerData, worldState]);

  useEffect(() => {
    // Single cleanup function that uses refs for current values
    return () => {
      const currentUserVal = currentUserRef.current;
      const isGuestPlayerVal = isGuestPlayerRef.current;
      const playerDataVal = playerDataRef.current;
      const worldStateVal = worldStateRef.current;
      
      // Save immediately when component unmounts
      if (currentUserVal && !isGuestPlayerVal && playerDataVal && playerDataVal.id) {
        const charDocRef = doc(db, "users", currentUserVal.uid, "characters", playerDataVal.id);
        const dataToSave = { 
          ...playerDataVal, 
          day: worldStateVal.day, 
          currentTimeInMinutes: worldStateVal.currentTimeInMinutes 
        };
        setDoc(charDocRef, dataToSave, { merge: true }).then(() => {
          console.log(`[Layout] Cleanup saved character ${playerDataVal.name} (ID: ${playerDataVal.id}) - Day ${worldStateVal.day}.`);
        }).catch(error => {
          console.error("[Layout] Error in cleanup save:", error);
        });
      }
      
      if (isGuestPlayerVal && playerDataVal) {
        localStorage.setItem('playerData_guest', JSON.stringify({ ...playerDataVal, day: worldStateVal.day, currentTimeInMinutes: worldStateVal.currentTimeInMinutes }));
        console.log(`[Layout] Cleanup saved guest game data to localStorage (Day ${worldStateVal.day}).`);
      }
    };
  }, []); // Empty dependency array - only create cleanup function once!


  // Update player stats when skills, achievements, or equipment change
  useEffect(() => {
    if (playerData && playerData.skills) {
      const calculatedStats = calculateTotalPlayerStats(playerData, ItemData);
      const expectedCapacity = 50 + (calculatedStats.strength * 5);
      
      // Check if we need to update stats, carry capacity, or health/mana/energy maximums
      const needsUpdate = 
        playerData.carryCapacity !== expectedCapacity || 
        JSON.stringify(playerData.stats) !== JSON.stringify(calculatedStats) ||
        playerData.health?.max !== calculatedStats.health?.max ||
        playerData.mana?.max !== calculatedStats.mana?.max ||
        playerData.energy?.max !== calculatedStats.energy?.max;
      
      if (needsUpdate) {
        setPlayerData(prev => ({
          ...prev,
          stats: calculatedStats,
          carryCapacity: expectedCapacity,
          health: {
            current: Math.min(prev.health?.current || calculatedStats.health.max, calculatedStats.health.max),
            max: calculatedStats.health.max
          },
          mana: {
            current: Math.min(prev.mana?.current || calculatedStats.mana.max, calculatedStats.mana.max),
            max: calculatedStats.mana.max
          },
          energy: {
            current: Math.min(prev.energy?.current || calculatedStats.energy.max, calculatedStats.energy.max),
            max: calculatedStats.energy.max
          }
                  }));
        }
    }
  }, [playerData?.skills, playerData?.completedAchievements, playerData?.equipment]);


  const passTimeAndUpdateState = useCallback((minutesToAdd) => {
    setWorldState(prevWorld => {
        if (prevWorld.isPaused || prevWorld._passedOut || isProcessingEndOfDay) {
            return prevWorld;
        }

        let newTimeInDay = prevWorld.currentTimeInMinutes + minutesToAdd;
        let newPassedOutState = prevWorld._passedOut;
        let newIsPausedState = prevWorld.isPaused;

        if (newTimeInDay >= MAX_DAY_MINUTES) {
            newPassedOutState = true;
            newIsPausedState = true; 
            newTimeInDay = MAX_DAY_MINUTES; 
            console.log("[Layout] Time reached MAX_DAY_MINUTES, marking for pass out and pausing.");
        }
      
        return { 
            ...prevWorld, 
            currentTimeInMinutes: newTimeInDay,
            _passedOut: newPassedOutState,
            isPaused: newIsPausedState 
        };
    });
  }, [isProcessingEndOfDay]);

  // Achievement Modal Handlers
  const showAchievementNotification = useCallback((achievement) => {
    setAchievementQueue(prev => [...prev, achievement]);
  }, []);

  const processAchievementQueue = useCallback(() => {
    if (achievementQueue.length > 0 && !showAchievementModal) {
      const nextAchievement = achievementQueue[0];
      setCurrentAchievement(nextAchievement);
      setShowAchievementModal(true);
      setAchievementQueue(prev => prev.slice(1));
    }
  }, [achievementQueue, showAchievementModal]);

  const handleAchievementModalClose = useCallback(() => {
    setShowAchievementModal(false);
    setCurrentAchievement(null);
    // Process next achievement in queue after a brief delay
    setTimeout(() => {
      processAchievementQueue();
    }, 500);
  }, [processAchievementQueue]);
  
  // Mobile Menu Handlers
  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuVisible(prev => !prev);
  }, []);
  
  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuVisible(false);
  }, []);

  // Settings Handlers
  const toggleAdminPanel = useCallback((enabled) => {
    setIsAdminEnabled(enabled);
    localStorage.setItem('adminPanelEnabled', JSON.stringify(enabled));
  }, []);

  const toggleTooltips = useCallback((enabled) => {
    setTooltipsEnabled(enabled);
    localStorage.setItem('tooltipsEnabled', JSON.stringify(enabled));
  }, []);

  const openSettingsModal = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const closeSettingsModal = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  // Process achievement queue whenever it changes
  useEffect(() => {
    processAchievementQueue();
  }, [processAchievementQueue]);

  const handleRestDay = useCallback((isForcedPassOut = false) => {
    if (isProcessingEndOfDay) {
        console.log("[Layout] handleRestDay: Already processing end of day, skipping.");
        return;
    }
    setIsProcessingEndOfDay(true); 

    const dayThatEnded = worldState.day;
    console.log(`[Layout] handleRestDay called for day ${dayThatEnded}. Forced Pass Out: ${isForcedPassOut}`);

    setWorldState(prevWorld => ({
        ...prevWorld,
        isPaused: true, 
        _passedOut: false, 
    }));

    // Handle expiration messages outside of state updater to avoid React error
    const expiredQuests = [];
    const expiredOrders = [];
    
    setPlayerData(prevData => {
        const updatedActiveQuests = prevData.activeQuests.map(q => ({ ...q, daysToComplete: q.daysToComplete - 1 }))
            .filter(q => {
                if (q.daysToComplete <= 0) {
                    expiredQuests.push(q.title);
                    return false;
                }
                return true;
            });
        const updatedCustomerOrders = prevData.customerOrders.map(o => ({ ...o, daysToComplete: o.daysToComplete - 1 }))
            .filter(o => {
                if (o.daysToComplete <= 0) {
                    expiredOrders.push(o.customerName);
                    return false;
                }
                return true;
            });

        const newHealth = isForcedPassOut ? Math.floor(prevData.health.max * 0.5) : prevData.health.max;
        const newMana = isForcedPassOut ? Math.floor(prevData.mana.max * 0.5) : prevData.mana.max;
        const newEnergy = isForcedPassOut ? Math.floor(prevData.energy.max * 0.5) : prevData.energy.max;
        
        // Track daily rest achievements
        let updatedPlayerStats = { ...prevData.playerStats };
        let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
        
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'daysRested', 1);
        
        // Reset daily quest/order management tracking
        const newDailyTracking = {
            questsPurchased: 0,
            ordersPurchased: 0,
            lastResetDay: dayThatEnded + 1
        };
        
        // Reset daily consumable purchase tracking
        const newConsumableTracking = {
            healingPotion: 0,
            energyPotion: 0,
            manaPotion: 0,
            bandage: 0,
            apple: 0,
            berries: 0,
            lastResetDay: dayThatEnded + 1
        };
        
        const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
        if (newAchievements.length > 0) {
          updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
          // Show achievement notifications
          newAchievements.forEach(achievement => {
            showAchievementNotification(achievement);
          });
        }
        
        return {
          ...prevData,
          health: { ...prevData.health, current: newHealth },
          mana: { ...prevData.mana, current: newMana },
          energy: { ...prevData.energy, current: newEnergy },
          stats: {
            ...prevData.stats,
            health: { ...prevData.stats.health, current: newHealth },
          },
          currentLocation: 'home', tripInventory: {}, currentCarryWeight: 0,
                      activeQuests: updatedActiveQuests,
            customerOrders: updatedCustomerOrders,
            playerStats: updatedPlayerStats,
            completedAchievements: updatedCompletedAchievements,
            dailyQuestOrderManagement: newDailyTracking,
            dailyConsumablePurchases: newConsumableTracking,
          gold: prevData.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
        };
    });

    // Add messages after state update to avoid React error
    setTimeout(() => {
        expiredQuests.forEach(title => addMessage(`Quest "${title}" expired.`, 'warning'));
        expiredOrders.forEach(customerName => addMessage(`Order for ${customerName} expired.`, 'warning'));
        
        if (isForcedPassOut) {
            addMessage("You passed out from exhaustion!", 'error');
        } else {
            addMessage("Resting for the day...", 'info');
        }
    }, 0);

    setBuyBackList([]);
    setBossEncounterQueue([]);
    setCurrentBossEncounterTier(null);
    setIsBossWaveActive(false);
    setCurrentWaveNumber(0);

    setEndOfDayData({
        dayNumber: dayThatEnded, 
        summary: { goldEarned: 0, xpGained: 0, itemsFound: 0 }, 
        isPassingOut: isForcedPassOut,
    });
    setShowEndOfDayModal(true);
    console.log(`[Layout] Showing EndOfDayModal for Day ${dayThatEnded}`);

  }, [addMessage, worldState.day, isProcessingEndOfDay]);

  const handleStartNextDayFromModal = () => {
    setShowEndOfDayModal(false);
    const dayThatJustEnded = endOfDayData.dayNumber;

    let messageToAdd = '';
    
    // Fix crafting times for day rollover before updating world state
    setPlayerData(prevData => {
      if (!prevData?.buildings) return prevData;
      
      const newBuildings = { ...prevData.buildings };
      let craftingAdjusted = false;
      
      // Adjust active crafting jobs for day rollover
      Object.entries(newBuildings).forEach(([buildingKey, buildingData]) => {
        if (buildingData?.activeCrafting) {
          const crafting = buildingData.activeCrafting;
          const currentTime = worldState.currentTimeInMinutes;
          
          // Calculate how much time has elapsed
          const elapsedTime = currentTime - crafting.startTime;
          
          // Calculate how much time should remain
          const remainingTime = crafting.duration - elapsedTime;
          
          if (remainingTime > 0) {
            // Adjust startTime for the new day (reset time to 0, but preserve progress)
            newBuildings[buildingKey] = {
              ...buildingData,
              activeCrafting: {
                ...crafting,
                startTime: -remainingTime, // Negative start time means it started "before" the new day
                progress: elapsedTime / crafting.duration // Preserve current progress
              }
            };
            craftingAdjusted = true;
            console.log(`[Layout] Adjusted crafting time for ${buildingKey}: ${remainingTime} minutes remaining`);
          }
        }
      });
      
      if (craftingAdjusted) {
        return { ...prevData, buildings: newBuildings };
      }
      
      return prevData;
    });
    
    setWorldState(prevWorld => {
        // Only advance the day if worldState.day still matches the day the modal was opened for.
        // This acts as a guard against multiple increments if the function is somehow re-triggered
        // before the state fully settles.
        const newDayNumber = prevWorld.day === dayThatJustEnded ? prevWorld.day + 1 : prevWorld.day;
        
        if (prevWorld.day === dayThatJustEnded) {
            console.log(`[Layout] Starting next day: ${newDayNumber}. Previous day was ${prevWorld.day}`);
            messageToAdd = `Day ${newDayNumber} begins. Your progress has been saved.`;
        } else {
            console.warn(`[Layout] handleStartNextDayFromModal: worldState.day (${prevWorld.day}) already advanced past modal's ended day (${dayThatJustEnded}). Current day remains ${prevWorld.day}.`);
            // If day was already advanced, still log something to indicate modal closure for current day
            messageToAdd = `Resuming Day ${prevWorld.day}.`;
        }
        
        return {
            ...prevWorld,
            day: newDayNumber,
            currentTimeInMinutes: 0,
            isPaused: false,
            _passedOut: false 
        };
    });

    // Add message after state update to avoid React error
    setTimeout(() => {
        if (messageToAdd) {
            addMessage(messageToAdd, worldState.day === dayThatJustEnded ? 'success' : 'info');
        }
    }, 0);
    
    // Force save immediately after day advancement (no debounce)
    setTimeout(() => {
        saveGameData(true); // Force immediate save
    }, 100);
    
    setIsProcessingEndOfDay(false); // Reset flag
  };

  const handleTogglePause = useCallback(() => {
    // Don't allow manual pause/unpause during activities that auto-pause
    if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || isProcessingEndOfDay) {
        addMessage("Cannot pause/unpause during this activity.", "warning");
        return;
    }
    
    setWorldState(prevWorld => {
        const newPauseState = !prevWorld.isPaused;
        // Add message after state update to avoid React error
        setTimeout(() => {
            if (newPauseState) {
                addMessage("Game paused.", "info");
            } else {
                addMessage("Game resumed.", "info");
            }
        }, 0);
        
        return {
            ...prevWorld,
            isPaused: newPauseState
        };
    });
  }, [isCombatActive, isFishingMinigameActive, isMiningMinigameActive, isForagingMinigameActive, isWoodChoppingMinigameActive, isDungeonMiningMinigameActive, isBossWaveActive, showEndOfDayModal, isProcessingEndOfDay, addMessage]);


  
  // ... (initiateCombat, startNextBossEncounterFight, handleCombatEnd, performPlayerAction, goToLocation)
  // ... (handleEquipItem, handleUnequipItem, handleUseItem, handleBuyItem, handleSellItem_Robust, handleBuyBackItem)
  // ... (handleUpgradeBuilding, handleCompleteQuest, handleFulfillOrder, handleFishingEnd)
  // ... (generateNewQuest, generateNewCustomerOrder, and their useEffect hooks)
  // The following functions are assumed to exist and are not repeated for brevity.
    const initiateCombat = useCallback((monsterKey, requiredEnergy, isFromBossWave = false) => {
    if (!playerData) return false;
    if (!isFromBossWave && isBossWaveActive) {
        addMessage("Cannot start a new fight during a boss encounter sequence.", "error");
        return false;
    }
    if (playerData.energy.current < requiredEnergy) {
      addMessage(`Not enough energy to fight! Need ${requiredEnergy}E.`, 'warning');
      return false;
    }
    const baseMonsterData = MonsterData[monsterKey];
    if (!baseMonsterData) {
      addMessage(`Error: Monster ${monsterKey} not found for combat.`, 'error');
      return false;
    }
    setPlayerData(prev => ({
      ...prev,
      energy: { ...prev.energy, current: prev.energy.current - requiredEnergy }
    }));
    addMessage(`Spent ${requiredEnergy} energy to start combat.`, 'info');
    let playerImage = '/img/player/playerSword.png';
    const mainHandItemId = playerData.equipment.mainHand;
    if (mainHandItemId) {
      const equippedItemInstance = playerData.inventory[mainHandItemId];
      if (equippedItemInstance && typeof equippedItemInstance === 'object') {
        const itemBase = ItemData[equippedItemInstance.key];
        if (itemBase && itemBase.weaponType) {
          switch (itemBase.weaponType) {
            case 'sword': playerImage = '/img/player/playerSword.png'; break;
            case 'bow': playerImage = '/img/player/playerBow.png'; break;
            case 'staff': playerImage = '/img/player/playerStaff.png'; break;
            default: break;
          }
        }
      }
    }
    const playerCombatStats = {
      name: playerData.name,
      image: playerImage,
      stats: {
          health: { current: playerData.health.current, max: playerData.health.max },
          damageMin: playerData.stats.damageMin,
          damageMax: playerData.stats.damageMax,
      },
    };
    let numEnemies = 1;
    if (!isFromBossWave) {
        numEnemies = getEnemyCountForRegularEncounter();
    }
    const monstersArray = [];
    for (let i = 0; i < numEnemies; i++) {
        monstersArray.push({
            key: monsterKey,
            instanceId: `${monsterKey}_${i}_${Date.now()}_${Math.random()}`,
        });
    }
    setCurrentCombatantPlayer(playerCombatStats);
    setCurrentCombatantMonsters(monstersArray);
    if (!isCombatActive) setIsCombatActive(true);
    setWorldState(prev => ({ ...prev, isPaused: true }));
    if (!isFromBossWave) {
        addMessage(`Encountered ${numEnemies} ${baseMonsterData.name}(s)!`, 'warning');
    }
    return true;
  }, [playerData, addMessage, isBossWaveActive, isCombatActive]);

  const startNextBossEncounterFight = useCallback(() => {
    if (bossEncounterQueue.length > 0) {
        const nextMonsterKey = bossEncounterQueue[0];
        const monsterData = MonsterData[nextMonsterKey];
        const energyCost = monsterData?.isBoss && bossEncounterQueue.length === 1
                           ? (gameActionsConfig.fightMonster?.energyCostBoss || 10)
                           : (gameActionsConfig.fightMonster?.energyCostWave || 3);
        const isTheActualBoss = monsterData?.isBoss && bossEncounterQueue.length === 1;
        if (isTheActualBoss) {
            addMessage(`Final Boss: ${monsterData?.name || 'The Boss'} appears!`, 'event_major');
        } else {
            addMessage(`Boss Encounter Wave ${currentWaveNumber} of ${BOSS_WAVE_COUNT}: ${monsterData?.name || 'Unknown'}!`, 'warning');
        }
        initiateCombat(nextMonsterKey, energyCost, true);
    } else {
        setIsBossWaveActive(false);
        setCurrentBossEncounterTier(null);
        setCurrentWaveNumber(0);
        setIsCombatActive(false);
        setWorldState(prev => ({ ...prev, isPaused: false }));
        addMessage("Boss encounter sequence concluded or queue empty.", "info");
    }
  }, [bossEncounterQueue, addMessage, initiateCombat, currentWaveNumber]);

  useEffect(() => {
    if (isBossWaveActive && bossEncounterQueue.length > 0 && currentWaveNumber === 1 && !isCombatActive) {
        startNextBossEncounterFight();
    }
  }, [isBossWaveActive, bossEncounterQueue, currentWaveNumber, isCombatActive, startNextBossEncounterFight]);

  const handleCombatEnd = useCallback((result) => {
    if (!playerData) return;
    const defeatedMonsterKeyInWave = bossEncounterQueue.length > 0 ? bossEncounterQueue[0] : null;

    if (result.finalPlayerHealth !== undefined) {
      setPlayerData(prevData => ({
        ...prevData,
        health: { ...prevData.health, current: result.finalPlayerHealth },
        stats: { ...prevData.stats, health: { ...prevData.stats.health, current: result.finalPlayerHealth } }
      }));
    }
   
    if (result.outcome === 'victory') {
        const monsterName = MonsterData[defeatedMonsterKeyInWave]?.name || 'opponent';
        addMessage(`Victory against ${monsterName}!`, 'success');
       
        let totalXpGained = 0;
        if (result.xpArray) {
            result.xpArray.forEach(xp => totalXpGained += xp);
        }
        if (totalXpGained > 0) {
            setPlayerData(prev => {
                const newSkills = {...prev.skills};
                if (newSkills.combat) newSkills.combat = updateSkillXP(newSkills.combat, totalXpGained);
                if (newSkills.attack) newSkills.attack = updateSkillXP(newSkills.attack, Math.floor(totalXpGained / 2));
                
                // Track combat achievements
                let updatedPlayerStats = { ...prev.playerStats };
                let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
                let newAchievements = [];
                
                if (currentCombatantMonsters && currentCombatantMonsters.length > 0) {
                    const defeatedMonsterKeys = currentCombatantMonsters.map(m => m.key);
                    const combatResult = {
                        outcome: 'victory',
                        monstersDefeated: defeatedMonsterKeys,
                        damageDealt: result.damageDealt || 0,
                        survivedAtLowHealth: result.finalPlayerHealth < 20
                    };
                    
                    updatedPlayerStats = updateCombatStats(updatedPlayerStats, combatResult);
                    newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
                    updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
                    
                    if (newAchievements.length > 0) {
                        newAchievements.forEach(achievement => {
                            showAchievementNotification(achievement);
                        });
                    }
                }
                
                const goldFromAchievements = newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0);
                
                // Track individual monster kills for progression system
                let updatedMonsterKills = { ...prev.monsterKills };
                if (currentCombatantMonsters && currentCombatantMonsters.length > 0) {
                    const defeatedMonsterKeys = currentCombatantMonsters.map(m => m.key);
                    defeatedMonsterKeys.forEach(monsterKey => {
                        updatedMonsterKills[monsterKey] = (updatedMonsterKills[monsterKey] || 0) + 1;
                    });
                }
                
                return { 
                    ...prev, 
                    skills: newSkills,
                    playerStats: updatedPlayerStats,
                    completedAchievements: updatedCompletedAchievements,
                    gold: prev.gold + goldFromAchievements,
                    monsterKills: updatedMonsterKills
                };
            });
            addMessage(`Gained ${totalXpGained} combat XP!`, 'success');
        }

        // Process quest progress updates for monster kills
        let questUpdates = [];
        if (currentCombatantMonsters && currentCombatantMonsters.length > 0) {
            const defeatedMonsterKeys = currentCombatantMonsters.map(m => m.key);
            
            setPlayerData(prevData => {
                const newActiveQuests = prevData.activeQuests.map(quest => {
                    if (quest.type === 'kill') {
                        const monsterKillCount = getMonsterKillCount(defeatedMonsterKeys, quest.target);
                        if (monsterKillCount > 0) {
                            const newAmount = (quest.currentAmount || 0) + monsterKillCount;
                            questUpdates.push({ 
                                title: quest.title, 
                                newAmount, 
                                requiredAmount: quest.requiredAmount,
                                monsterKillCount 
                            });
                            return { ...quest, currentAmount: newAmount };
                        }
                    } else if (quest.type === 'mixed') {
                        // Handle mixed quests - update kill requirements
                        let questUpdated = false;
                        const newRequirements = quest.requirements.map(req => {
                            if (req.type === 'kill') {
                                const monsterKillCount = getMonsterKillCount(defeatedMonsterKeys, req.target);
                                if (monsterKillCount > 0) {
                                    const newAmount = (req.currentAmount || 0) + monsterKillCount;
                                    questUpdated = true;
                                    return { ...req, currentAmount: newAmount };
                                }
                            }
                            return req;
                        });
                        
                        if (questUpdated) {
                            questUpdates.push({ 
                                title: quest.title, 
                                type: 'mixed',
                                requirements: newRequirements
                            });
                            return { ...quest, requirements: newRequirements };
                        }
                    }
                    return quest;
                });
                return { ...prevData, activeQuests: newActiveQuests };
            });
        }

        if (result.lootArray && result.lootArray.length > 0) {
            const collectedLoot = {};
            result.lootArray.forEach(monsterLoot => {
                 if (monsterLoot && typeof monsterLoot === 'object') {
                    for (const [itemKey, quantity] of Object.entries(monsterLoot)) {
                        collectedLoot[itemKey] = (collectedLoot[itemKey] || 0) + quantity;
                    }
                }
            });
            setPlayerData(prevData => {
                const newTripInventory = { ...prevData.tripInventory };
                let newGold = prevData.gold;
                let lootMessages = [];
                let totalWeight = 0;
                
                for (const [itemKey, quantity] of Object.entries(collectedLoot)) {
                    const itemDetails = ItemData[itemKey];
                    if (itemDetails) {
                        if (itemDetails.stackable) {
                            newTripInventory[itemKey] = (newTripInventory[itemKey] || 0) + quantity;
                            totalWeight += (itemDetails.weight || 0) * quantity;
                        } else {
                            for (let i = 0; i < quantity; i++) {
                                const newItemInstance = createInitialItemInstance(itemKey);
                                if (newItemInstance) {
                                    newTripInventory[newItemInstance.id] = newItemInstance;
                                    totalWeight += itemDetails.weight || 0;
                                }
                            }
                        }
                        lootMessages.push(`${itemDetails.name} x${quantity}`);
                    } else if (itemKey === 'gold') {
                        newGold += quantity;
                        lootMessages.push(`Gold x${quantity}`);
                    }
                }
                
                const newCarryWeight = prevData.currentCarryWeight + totalWeight;
                if (newCarryWeight > prevData.carryCapacity) {
                    addMessage(`Loot obtained but too heavy to carry! Items dropped: ${lootMessages.join(', ')}.`, 'warning');
                    return { ...prevData, gold: newGold }; // Only keep gold if over capacity
                }
                
                if (lootMessages.length > 0) {
                    addMessage(`Loot obtained: ${lootMessages.join(', ')}.`, 'success');
                }
                return { 
                    ...prevData, 
                    tripInventory: newTripInventory, 
                    currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                    gold: newGold 
                };
            });
        }

        // Display quest progress updates
        setTimeout(() => {
            questUpdates.forEach(update => {
                if (update.type === 'mixed') {
                    const killUpdates = update.requirements.filter(req => req.type === 'kill' && req.currentAmount);
                    killUpdates.forEach(killReq => {
                        // eslint-disable-next-line no-unused-vars
                        const monsterName = getMonsterTypeDisplayName(update.title);
                        addMessage(`Quest Update: "${update.title}" - Defeated ${killReq.target} (${killReq.currentAmount}/${killReq.amount})`, 'neutral');
                    });
                } else {
                    const monsterName = getMonsterTypeDisplayName(update.title);
                    addMessage(`Quest Update: "${update.title}" - Defeated ${update.monsterKillCount} ${monsterName} (${update.newAmount}/${update.requiredAmount})`, 'neutral');
                }
            });
        }, 0);

        if (isBossWaveActive) {
            const newQueue = bossEncounterQueue.slice(1);
            setBossEncounterQueue(newQueue);
           
            if (newQueue.length > 0) {
                setCurrentWaveNumber(prev => prev + 1);
                setTimeout(() => startNextBossEncounterFight(), 1500);
            } else {
                setIsBossWaveActive(false);
                const defeatedBossTier = currentBossEncounterTier;
                setCurrentBossEncounterTier(null);
                setCurrentWaveNumber(0);
               
                setPlayerData(prev => {
                    const newUnlockedTier = Math.max(prev.unlockedTier, defeatedBossTier + 1);
                    if (newUnlockedTier > prev.unlockedTier) {
                         addMessage(`VICTORY! Tier ${defeatedBossTier} boss defeated! Tier ${newUnlockedTier} unlocked!`, 'event_major');
                    } else {
                        addMessage(`Victory! Tier ${defeatedBossTier} boss defeated again!`, 'success');
                    }
                    return {...prev, unlockedTier: newUnlockedTier };
                });
                setIsCombatActive(false);
                setCurrentCombatantPlayer(null);
                setCurrentCombatantMonsters([]);
                setWorldState(prev => ({ ...prev, isPaused: false }));
            }
        } else {
             setIsCombatActive(false);
             setCurrentCombatantPlayer(null);
             setCurrentCombatantMonsters([]);
             setWorldState(prev => ({ ...prev, isPaused: false }));
        }

    } else { 
         if (result.outcome === 'defeat') {
            const deathMessages = [
                "You have fallen in battle and collapsed to the ground...",
                "Darkness overtakes you as you succumb to your wounds...",
                "Your strength fails you and you lose consciousness...",
                "The last thing you see is the ground rushing up to meet you...",
                "Your vision fades to black as you fall defeated..."
            ];
            const randomDeathMessage = deathMessages[Math.floor(Math.random() * deathMessages.length)];
            addMessage(randomDeathMessage, 'error');
            addMessage("You awaken at home, battered and bruised. All items in your haul have been lost.", 'warning');
            
            // Track death achievements
            setPlayerData(prev => {
                let updatedPlayerStats = { ...prev.playerStats };
                let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
                
                const combatResult = {
                    outcome: 'defeat',
                    died: true,
                    damageDealt: result.damageDealt || 0
                };
                
                updatedPlayerStats = updateCombatStats(updatedPlayerStats, combatResult);
                const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
                updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
                
                if (newAchievements.length > 0) {
                    newAchievements.forEach(achievement => {
                        showAchievementNotification(achievement);
                    });
                }
                
                return { 
                    ...prev, 
                    playerStats: updatedPlayerStats,
                    completedAchievements: updatedCompletedAchievements,
                    gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
                };
            });
         } else if (result.outcome === 'fled') {
            addMessage('You fled from combat.', 'warning');
         }

         if (isBossWaveActive) {
            setIsBossWaveActive(false);
            setCurrentBossEncounterTier(null);
            setBossEncounterQueue([]);
            setCurrentWaveNumber(0);
            if (result.outcome !== 'victory') {
                 addMessage(result.outcome === 'defeat' ? "Boss challenge failed." : "Fled from boss challenge.", result.outcome === 'defeat' ? "error" : "warning");
            }
         }
        
         if (playerData.health.current <= 0 || (result.finalPlayerHealth !== undefined && result.finalPlayerHealth <= 0)) {
            if (!worldState._passedOut && !showEndOfDayModal && !isProcessingEndOfDay) { 
                handleRestDay(true); 
            }
         }
         setIsCombatActive(false);
         setCurrentCombatantPlayer(null);
         setCurrentCombatantMonsters([]);
         setWorldState(prev => ({ ...prev, isPaused: false }));
    }
  }, [addMessage, playerData, handleRestDay, isBossWaveActive, bossEncounterQueue, currentBossEncounterTier, startNextBossEncounterFight, worldState._passedOut, showEndOfDayModal, isProcessingEndOfDay]);

  const performPlayerAction = useCallback((actionKey, payload = {}) => {
    if (!playerData || showEndOfDayModal || isProcessingEndOfDay) return;
    if (isBossWaveActive && actionKey !== 'challengeTierBoss' && actionKey !== 'fightMonster') {
        addMessage("Cannot perform other actions during a boss encounter!", "warning");
        return;
    }
    const actionConfig = gameActionsConfig[actionKey];
    if (!actionConfig && actionKey !== 'challengeTierBoss') {
        addMessage(`Error: Action ${actionKey} not found.`, 'error');
        return;
    }
    if (actionKey === 'fightMonster' && payload.monsterKey) {
      initiateCombat(payload.monsterKey, gameActionsConfig.fightMonster?.energyCost || 5, false);
      return;
    }
    if (actionKey === 'challengeTierBoss' && payload.tier && payload.bossKey) {
        const actualBossData = MonsterData[payload.bossKey];
        if (!actualBossData || !actualBossData.isBoss) {
            addMessage("Invalid boss key or target is not a boss.", "error");
            return;
        }
        addMessage(`Preparing to challenge Tier ${payload.tier} boss: ${actualBossData.name}...`, 'event');
        const regularMonstersForTier = Object.values(MonsterData).filter(
            m => m.tier === payload.tier && !m.isBoss && m.key !== payload.bossKey
        );
        const waveMonsterKeys = [];
        if (regularMonstersForTier.length > 0) {
            for (let i = 0; i < BOSS_WAVE_COUNT; i++) {
                waveMonsterKeys.push(regularMonstersForTier[Math.floor(Math.random() * regularMonstersForTier.length)].key);
            }
        } else {
             addMessage(`No regular wave monsters found for Tier ${payload.tier}. Proceeding directly to boss.`, "warning");
        }
        const finalQueue = [...waveMonsterKeys, payload.bossKey];
        setBossEncounterQueue(finalQueue);
        setCurrentBossEncounterTier(payload.tier);
        setCurrentWaveNumber(1);
        setWorldState(prev => ({ ...prev, isPaused: true }));
        setIsBossWaveActive(true);
        return;
    }
    if (actionKey === 'startFishing') {
        const mainHandItemId = playerData.equipment.mainHand;
        const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
        if (!tool || typeof tool !== 'object' || tool.toolType !== 'fishingRod') {
          addMessage(`A fishing rod must be equipped.`, 'warning'); return;
        }
        if (tool.durability <= 0) { addMessage(`Your ${tool.name} is broken!`, 'error'); return; }
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to fish!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 5);
        setIsFishingMinigameActive(true);
        setWorldState(prev => ({ ...prev, isPaused: true }));
        addMessage("You cast your line...", 'info');
        return;
    }
    
    if (actionKey === 'mineOre' && actionConfig.startsMiniGame === 'mining') {
        const mainHandItemId = playerData.equipment.mainHand;
        const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
        if (!tool || typeof tool !== 'object' || tool.toolType !== 'pickaxe') {
          addMessage(`A pickaxe must be equipped.`, 'warning'); return;
        }
        if (tool.durability <= 0) { addMessage(`Your ${tool.name} is broken!`, 'error'); return; }
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to mine!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 5);
        setIsMiningMinigameActive(true);
        setWorldState(prev => ({ ...prev, isPaused: true }));
        addMessage("You start mining...", 'info');
        return;
    }
    
    if (actionKey === 'forage' && actionConfig.startsMiniGame === 'foraging') {
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to forage!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 5);
        setIsForagingMinigameActive(true);
        setWorldState(prev => ({ ...prev, isPaused: true }));
        addMessage("You start searching for edibles...", 'info');
        return;
    }
    
    if (actionKey === 'startFarming' && actionConfig.startsMiniGame === 'farming') {
        // Check if the player owns the required building
        if (actionConfig.requiredBuilding && !playerData.buildings?.[actionConfig.requiredBuilding]?.owned) {
            addMessage("You need to purchase Farming from the shop first!", 'warning');
            return;
        }
        
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to start farming!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 5);
        setIsFarmingMinigameActive(true);
        // Don't pause the game - farming continues in real time
        addMessage("You head to your farm plot...", 'info');
        return;
    }
    
    if (actionKey === 'dungeonMining' && actionConfig.startsMiniGame === 'dungeonMining') {
        const mainHandItemId = playerData.equipment.mainHand;
        const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
        if (!tool || typeof tool !== 'object' || tool.toolType !== 'pickaxe') {
          addMessage(`A pickaxe must be equipped to enter the dungeon mines.`, 'warning'); return;
        }
        if (tool.durability <= 0) { addMessage(`Your ${tool.name} is broken!`, 'error'); return; }
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to enter the dungeon mines!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 0);
        setIsDungeonMiningMinigameActive(true);
        // Don't pause time - let it continue running during dungeon mining
        addMessage("You descend into the dangerous dungeon mines...", 'info');
        return;
    }
    
    if (actionKey === 'chopWood' && actionConfig.startsMiniGame === 'woodChopping') {
        const mainHandItemId = playerData.equipment.mainHand;
        const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
        if (!tool || typeof tool !== 'object' || tool.toolType !== 'axe') {
          addMessage(`An axe must be equipped.`, 'warning'); return;
        }
        if (tool.durability <= 0) { addMessage(`Your ${tool.name} is broken!`, 'error'); return; }
        if (playerData.energy.current < (actionConfig.energyCost || 0)) {
            addMessage("Not enough energy to chop wood!", 'warning'); return;
        }
        if (actionConfig.energyCost && actionConfig.energyCost > 0) {
            setPlayerData(prev => ({ ...prev, energy: { ...prev.energy, current: prev.energy.current - actionConfig.energyCost }}));
        }
        passTimeAndUpdateState(actionConfig.timeCost || 5);
        setIsWoodChoppingMinigameActive(true);
        setWorldState(prev => ({ ...prev, isPaused: true }));
        addMessage("You start chopping wood...", 'info');
        return;
    }
    // Handle refining actions (furnace, forge, sawmill, workbench) - Real-time crafting system
    if (actionConfig.type === 'refining' || actionConfig.type === 'crafting') {
      // Check if player has required building
      if (actionConfig.buildingRequired && !playerData.buildings?.[actionConfig.buildingRequired]?.owned) {
        addMessage(`You need a ${actionConfig.buildingRequired} to do this!`, 'warning');
        return;
      }
      
      // Check if building is already crafting
      const buildingData = playerData.buildings?.[actionConfig.buildingRequired];
      if (buildingData?.activeCrafting) {
        addMessage(`${actionConfig.buildingRequired} is already crafting something!`, 'warning');
        return;
      }
      
      // Check if player has required input materials
      if (actionConfig.input) {
        for (const [itemKey, requiredAmount] of Object.entries(actionConfig.input)) {
          const available = playerData.inventory[itemKey] || 0;
          if (available < requiredAmount) {
            const itemName = ItemData[itemKey]?.name || itemKey;
            addMessage(`Need ${requiredAmount - available} more ${itemName}!`, 'warning');
            return;
          }
        }
      }
      
      // Handle fuel requirements for furnace operations
      let selectedFuel = null;
      let fuelEfficiency = 0;
      
      if (actionConfig.buildingRequired === 'furnace' && actionConfig.fuel) {
        // Get fuel selection from payload or auto-select best available fuel
        selectedFuel = payload?.selectedFuel;
        
        if (!selectedFuel) {
          // Auto-select best available fuel (prefer charcoal over wood)
          const availableFuels = Object.keys(actionConfig.fuel).filter(fuelType => {
            const required = actionConfig.fuel[fuelType];
            const available = playerData.inventory[fuelType] || 0;
            return available >= required;
          });
          
          if (availableFuels.includes('charcoal')) {
            selectedFuel = 'charcoal';
          } else if (availableFuels.includes('wood')) {
            selectedFuel = 'wood';
          }
        }
        
        if (!selectedFuel) {
          const fuelOptions = Object.keys(actionConfig.fuel).map(key => ItemData[key]?.name || key).join(' or ');
          addMessage(`Need fuel to operate the furnace! Required: ${fuelOptions}`, 'warning');
          return;
        }
        
        // Check if player has enough of the selected fuel
        const requiredFuel = actionConfig.fuel[selectedFuel];
        const availableFuel = playerData.inventory[selectedFuel] || 0;
        if (availableFuel < requiredFuel) {
          const fuelName = ItemData[selectedFuel]?.name || selectedFuel;
          addMessage(`Need ${requiredFuel - availableFuel} more ${fuelName} for fuel!`, 'warning');
          return;
        }
        
        // Apply fuel efficiency bonus
        fuelEfficiency = selectedFuel === 'charcoal' ? 0.5 : 0; // Charcoal provides 50% time reduction
      }
      
      // Start the crafting process
      setPlayerData(prevData => {
        const newInventory = { ...prevData.inventory };
        const newBuildings = { ...prevData.buildings };
        
        // Remove input materials
        if (actionConfig.input) {
          for (const [itemKey, requiredAmount] of Object.entries(actionConfig.input)) {
            newInventory[itemKey] = (newInventory[itemKey] || 0) - requiredAmount;
            if (newInventory[itemKey] <= 0) {
              delete newInventory[itemKey];
            }
          }
        }
        
        // Consume fuel if required
        if (selectedFuel && actionConfig.fuel) {
          const requiredFuel = actionConfig.fuel[selectedFuel];
          newInventory[selectedFuel] = (newInventory[selectedFuel] || 0) - requiredFuel;
          if (newInventory[selectedFuel] <= 0) {
            delete newInventory[selectedFuel];
          }
        }
        
        // Calculate adjusted duration with fuel efficiency
        const baseDuration = actionConfig.duration || actionConfig.timeCost || 60;
        const adjustedDuration = Math.ceil(baseDuration * (1 - fuelEfficiency));
        
        // Set up the crafting job
        const craftingJob = {
          actionKey,
          actionConfig,
          startTime: worldState.currentTimeInMinutes,
          duration: adjustedDuration,
          continuousCrafting: false, // Can be toggled by user
          selectedFuel, // Store selected fuel for continuous crafting
          fuelEfficiency // Store efficiency for display
        };
        
        newBuildings[actionConfig.buildingRequired] = {
          ...newBuildings[actionConfig.buildingRequired],
          activeCrafting: craftingJob
        };
        
        return {
          ...prevData,
          inventory: newInventory,
          buildings: newBuildings
        };
      });
      
      const itemName = actionConfig.output ? 
        Object.keys(actionConfig.output).map(key => ItemData[key]?.name || key).join(', ') :
        ItemData[actionConfig.outputItemKey]?.name || actionConfig.outputItemKey;
      
      let fuelMessage = '';
      if (selectedFuel) {
        const fuelName = ItemData[selectedFuel]?.name || selectedFuel;
        const efficiencyText = fuelEfficiency > 0 ? ` (${Math.round(fuelEfficiency * 100)}% faster with ${fuelName})` : '';
        fuelMessage = ` using ${fuelName}${efficiencyText}`;
      }
      
      addMessage(`Started crafting ${itemName} in ${actionConfig.buildingRequired}${fuelMessage}!`, 'info');
      return;
    }
    
    if (actionConfig.energyCost && playerData.energy.current < actionConfig.energyCost) {
       addMessage("Not enough energy!", 'warning'); return;
    }
    addMessage(`Attempting: ${actionConfig.name || actionKey}...`, 'info');
    if (playerData.currentLocation !== 'wilderness' &&
        (actionConfig.type === 'gathering' || actionConfig.type === 'activity')) {
        addMessage(`Action "${actionConfig.name}" can only be done in wilderness.`, 'warning'); return;
    }
    if (actionConfig.requiredTool) {
      const mainHandItemId = playerData.equipment.mainHand;
      const tool = mainHandItemId ? playerData.inventory[mainHandItemId] : null;
      if (!tool || typeof tool !== 'object' || tool.toolType !== actionConfig.requiredTool) {
        addMessage(`A ${actionConfig.requiredTool} must be equipped.`, 'warning'); return;
      }
      if (tool.durability <= 0) { addMessage(`Your ${tool.name} is broken!`, 'error'); return; }
    }
    let energyToDeduct = actionConfig.energyCost || 0;
    let temporaryPlayerDataChanges = {
        energyReduction: energyToDeduct,
        inventoryChanges: {},
        tripInventoryChanges: {},
        carryWeightChange: 0,
        skillXpGains: actionConfig.xpGain ? { ...actionConfig.xpGain } : {},
        questProgressUpdates: []
    };
    let actionLogMessages = [];
    if (actionConfig.requiredTool && playerData.equipment.mainHand) {
      const toolId = playerData.equipment.mainHand;
      const toolInstance = playerData.inventory[toolId];
      if (toolInstance && toolInstance.durability !== undefined && Math.random() < DURABILITY_LOSS_CHANCE) {
        const newDurability = Math.max(0, toolInstance.durability - 1);
        temporaryPlayerDataChanges.inventoryChanges[toolId] = { ...toolInstance, durability: newDurability };
        actionLogMessages.push({ text: `${ItemData[toolInstance.key].name} durability: ${newDurability}/${toolInstance.maxDurability}.`, type: 'info' });
        if (newDurability <= 0) actionLogMessages.push({ text: `${ItemData[toolInstance.key].name} broke!`, type: 'error' });
      }
    }
    let gatheredSomethingOverall = false;
    if (actionConfig.baseYield) {
      const currentCarry = playerData.currentCarryWeight;
      for (const [itemKey, yieldInfo] of Object.entries(actionConfig.baseYield)) {
        if (Math.random() < (yieldInfo.chance || 1)) {
          const qty = Array.isArray(yieldInfo.quantity) ? (Math.floor(Math.random() * (yieldInfo.quantity[1] - yieldInfo.quantity[0] + 1)) + yieldInfo.quantity[0]) : (yieldInfo.quantity || 1);
          const itemDetails = ItemData[itemKey];
          if (itemDetails && qty > 0) {
            const weightOfThese = (itemDetails.weight || 0) * qty;
            if (currentCarry + temporaryPlayerDataChanges.carryWeightChange + weightOfThese <= playerData.carryCapacity) {
              temporaryPlayerDataChanges.tripInventoryChanges[itemKey] = (temporaryPlayerDataChanges.tripInventoryChanges[itemKey] || 0) + qty;
              temporaryPlayerDataChanges.carryWeightChange += weightOfThese;
              actionLogMessages.push({ text: `Found ${itemDetails.name} x${qty}.`, type: 'success' });
              gatheredSomethingOverall = true;
            } else actionLogMessages.push({ text: `Found ${itemDetails.name} x${qty} but couldn't carry.`, type: 'warning' });
          }
        }
      }
    }
    if (!gatheredSomethingOverall && actionConfig.baseYield && actionConfig.type === 'gathering') {
        actionLogMessages.push({ text: "Found nothing useful this time.", type: 'info' });
    }
    
    let questUpdates = [];
    
    setPlayerData(prevData => {
        let newEnergy = { ...prevData.energy, current: prevData.energy.current - temporaryPlayerDataChanges.energyReduction };
        let newInventory = { ...prevData.inventory, ...temporaryPlayerDataChanges.inventoryChanges };
        let newTripInventory = { ...prevData.tripInventory };
        Object.entries(temporaryPlayerDataChanges.tripInventoryChanges).forEach(([key, qty]) => {
            newTripInventory[key] = (newTripInventory[key] || 0) + qty;
        });
        let newCarryWeight = prevData.currentCarryWeight + temporaryPlayerDataChanges.carryWeightChange;
        let newSkills = { ...prevData.skills };
        Object.entries(temporaryPlayerDataChanges.skillXpGains).forEach(([skillId, xp]) => {
            if (newSkills[skillId]) newSkills[skillId] = updateSkillXP(newSkills[skillId], xp);
            else console.warn(`[performPlayerAction] Skill ID "${skillId}" not found in player data during XP gain.`);
        });
        let newActiveQuests = prevData.activeQuests.map(q => {
            const update = temporaryPlayerDataChanges.questProgressUpdates.find(u => u.questInstanceId === q.instanceId);
            if (update) {
                questUpdates.push({ title: q.title, newAmount: update.newAmount, requiredAmount: q.requiredAmount });
                return { ...q, currentAmount: update.newAmount };
            }
            return q;
        });
        return {
            ...prevData,
            energy: newEnergy,
            inventory: newInventory,
            tripInventory: newTripInventory,
            currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
            skills: newSkills,
            activeQuests: newActiveQuests,
        };
    });
    
    // Handle quest updates after state update to avoid React error
    setTimeout(() => {
        questUpdates.forEach(update => {
            addMessage(`Quest Update: "${update.title}" (${update.newAmount}/${update.requiredAmount})`, 'neutral');
        });
    }, 0);
    
    passTimeAndUpdateState(actionConfig.timeCost);
    
    // Handle action log messages after state update to avoid React error
    setTimeout(() => {
        actionLogMessages.forEach(msg => addMessage(msg.text, msg.type));
    }, 0);
  }, [playerData, addMessage, passTimeAndUpdateState, initiateCombat, isBossWaveActive, showEndOfDayModal, isProcessingEndOfDay, worldState]);

  // Handle special building actions
  const handleBuildingAction = useCallback((actionType, actionData) => {
    if (!playerData || showEndOfDayModal || isProcessingEndOfDay) return;
    
    switch (actionType) {
      case 'repairItem':
        if (!actionData || !actionData.itemId || !actionData.cost) return;
        
        if (playerData.gold < actionData.cost) {
          addMessage("Not enough gold to repair this item!", 'warning');
          return;
        }
        
        setPlayerData(prevData => {
          const newInventory = { ...prevData.inventory };
          const item = newInventory[actionData.itemId];
          
          if (item && typeof item === 'object' && item.durability !== undefined) {
            // Repair the item to full durability
            newInventory[actionData.itemId] = {
              ...item,
              durability: item.maxDurability
            };
            
            addMessage(`Repaired ${ItemData[item.key]?.name || 'item'} to full durability!`, 'success');
            
            return {
              ...prevData,
              inventory: newInventory,
              gold: prevData.gold - actionData.cost
            };
          }
          
          addMessage("Could not repair item - item not found or invalid.", 'error');
          return prevData;
        });
        break;
        
      case 'upgradeItem':
        if (!actionData || !actionData.itemId || !actionData.cost) return;
        
        if (playerData.gold < actionData.cost) {
          addMessage("Not enough gold to upgrade this item!", 'warning');
          return;
        }
        
        setPlayerData(prevData => {
          const newInventory = { ...prevData.inventory };
          const item = newInventory[actionData.itemId];
          
          if (item && typeof item === 'object') {
            // Upgrade the item - increase damage by 10%
            const upgradedItem = { ...item };
            if (upgradedItem.damage) {
              upgradedItem.damage = Math.ceil(upgradedItem.damage * 1.1);
            }
            if (upgradedItem.maxDurability) {
              upgradedItem.maxDurability = Math.ceil(upgradedItem.maxDurability * 1.1);
              upgradedItem.durability = Math.min(upgradedItem.durability, upgradedItem.maxDurability);
            }
            
            newInventory[actionData.itemId] = upgradedItem;
            
            addMessage(`Upgraded ${ItemData[item.key]?.name || 'item'} - improved stats!`, 'success');
            
            return {
              ...prevData,
              inventory: newInventory,
              gold: prevData.gold - actionData.cost
            };
          }
          
          addMessage("Could not upgrade item - item not found or invalid.", 'error');
          return prevData;
        });
        break;
        
      default:
        addMessage(`Unknown building action: ${actionType}`, 'error');
    }
  }, [playerData, addMessage, showEndOfDayModal, isProcessingEndOfDay]);

  // Process crafting progress when time advances - moved logic directly into useEffect to avoid infinite loops
  useEffect(() => {
    if (!worldState.currentTimeInMinutes || !playerData?.buildings) return;
    
    const currentTime = worldState.currentTimeInMinutes;
    
    setPlayerData(prevData => {
      const newBuildings = { ...prevData.buildings };
      const newInventory = { ...prevData.inventory };
      let newSkills = { ...prevData.skills };
      let craftingUpdates = [];
      let hasChanges = false;
      
      Object.entries(newBuildings).forEach(([buildingKey, buildingData]) => {
        if (buildingData?.activeCrafting) {
          const crafting = buildingData.activeCrafting;
          const elapsedTime = currentTime - crafting.startTime;
          const progress = Math.min(elapsedTime / crafting.duration, 1);
          
          // Only update if progress actually changed to avoid unnecessary re-renders
          if (Math.abs(progress - (crafting.progress || 0)) > 0.01) {
            hasChanges = true;
            
            // Update crafting progress
            newBuildings[buildingKey] = {
              ...buildingData,
              activeCrafting: {
                ...crafting,
                progress
              }
            };
            
            // Check if crafting is complete
            if (progress >= 1) {
              const actionConfig = crafting.actionConfig;
              
              // Add output materials
              if (actionConfig.output) {
                for (const [itemKey, outputAmount] of Object.entries(actionConfig.output)) {
                  const itemData = ItemData[itemKey];
                  if (itemData?.stackable) {
                    newInventory[itemKey] = (newInventory[itemKey] || 0) + outputAmount;
                  } else {
                    // For non-stackable items, create unique instances
                    for (let i = 0; i < outputAmount; i++) {
                      const newItem = createInitialItemInstance(itemKey);
                      if (newItem) {
                        newInventory[newItem.id] = newItem;
                      }
                    }
                  }
                }
              }
              
              // Handle crafting unique items (for forge/workbench)
              if (actionConfig.outputItemKey) {
                const newItem = createInitialItemInstance(actionConfig.outputItemKey);
                if (newItem) {
                  newInventory[newItem.id] = newItem;
                }
              }
              
              // Grant XP
              if (actionConfig.xpGain) {
                Object.entries(actionConfig.xpGain).forEach(([skillId, xp]) => {
                  if (newSkills[skillId]) {
                    newSkills[skillId] = updateSkillXP(newSkills[skillId], xp);
                  }
                });
              }
              
              // Prepare completion message
              const itemName = actionConfig.output ? 
                Object.entries(actionConfig.output).map(([key, amount]) => 
                  `${ItemData[key]?.name || key} x${amount}`
                ).join(', ') :
                ItemData[actionConfig.outputItemKey]?.name || actionConfig.outputItemKey;
              
              craftingUpdates.push(`Finished crafting ${itemName}!`);
              
              // Track crafting achievements
              let updatedPlayerStats = { ...prevData.playerStats };
              let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
              
              if (actionConfig.type === 'crafting') {
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsCrafted', 1);
                if (actionConfig.outputItemKey) {
                  const outputItem = ItemData[actionConfig.outputItemKey];
                  if (outputItem?.type === 'tool') {
                    updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'toolsCrafted', 1);
                  } else if (outputItem?.type === 'weapon') {
                    updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'weaponsCrafted', 1);
                  } else if (outputItem?.type === 'armor') {
                    updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'armorCrafted', 1);
                  }
                }
              } else if (actionConfig.type === 'refining') {
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsRefined', 1);
                // Track smelting activities
                if (buildingKey === 'furnace') {
                  if (actionConfig.output && Object.keys(actionConfig.output)[0]) {
                    const outputKey = Object.keys(actionConfig.output)[0];
                    if (outputKey.includes('Bar')) {
                      // Track ore smelting
                      if (outputKey === 'copperBar') {
                        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'copperOreSmelted', 1);
                      } else if (outputKey === 'ironBar') {
                        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'ironOreSmelted', 1);
                      }
                    }
                  }
                }
              }
              
              // Check for new achievements
              const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
              if (newAchievements.length > 0) {
                updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
                newAchievements.forEach(achievement => {
                  showAchievementNotification(achievement);
                });
              }
              
              // Update player data with new stats and achievements
              prevData.playerStats = updatedPlayerStats;
              prevData.completedAchievements = updatedCompletedAchievements;
              prevData.gold += newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0);
              
              // Check for continuous crafting
              if (crafting.continuousCrafting) {
                // Check if we can start another round
                let canContinue = true;
                let stopReason = '';
                
                // Check input materials
                if (actionConfig.input) {
                  for (const [itemKey, requiredAmount] of Object.entries(actionConfig.input)) {
                    const available = newInventory[itemKey] || 0;
                    if (available < requiredAmount) {
                      canContinue = false;
                      stopReason = `not enough ${ItemData[itemKey]?.name || itemKey}`;
                      break;
                    }
                  }
                }
                
                // Check fuel requirements for furnace operations
                if (canContinue && buildingKey === 'furnace' && actionConfig.fuel && crafting.selectedFuel) {
                  const requiredFuel = actionConfig.fuel[crafting.selectedFuel];
                  const availableFuel = newInventory[crafting.selectedFuel] || 0;
                  if (availableFuel < requiredFuel) {
                    canContinue = false;
                    stopReason = `not enough ${ItemData[crafting.selectedFuel]?.name || crafting.selectedFuel} fuel`;
                  }
                }
                
                if (canContinue) {
                  // Consume materials for next round
                  if (actionConfig.input) {
                    for (const [itemKey, requiredAmount] of Object.entries(actionConfig.input)) {
                      newInventory[itemKey] = (newInventory[itemKey] || 0) - requiredAmount;
                      if (newInventory[itemKey] <= 0) {
                        delete newInventory[itemKey];
                      }
                    }
                  }
                  
                  // Consume fuel for next round
                  if (buildingKey === 'furnace' && actionConfig.fuel && crafting.selectedFuel) {
                    const requiredFuel = actionConfig.fuel[crafting.selectedFuel];
                    newInventory[crafting.selectedFuel] = (newInventory[crafting.selectedFuel] || 0) - requiredFuel;
                    if (newInventory[crafting.selectedFuel] <= 0) {
                      delete newInventory[crafting.selectedFuel];
                    }
                  }
                  
                  // Start new crafting cycle
                  newBuildings[buildingKey].activeCrafting = {
                    ...crafting,
                    startTime: currentTime,
                    progress: 0
                  };
                  return; // Don't clear the crafting job
                } else {
                  craftingUpdates.push(`Continuous crafting stopped - ${stopReason}`);
                }
              }
              
              // Clear completed crafting job
              delete newBuildings[buildingKey].activeCrafting;
            }
          }
        }
      });
      
      // Only return new state if there were actual changes
      if (!hasChanges && craftingUpdates.length === 0) {
        return prevData;
      }
      
      // Show crafting completion messages
      if (craftingUpdates.length > 0) {
        setTimeout(() => {
          craftingUpdates.forEach(message => addMessage(message, 'success'));
        }, 0);
      }
      
      return {
        ...prevData,
        buildings: newBuildings,
        inventory: newInventory,
        skills: newSkills
      };
    });
  }, [worldState.currentTimeInMinutes, addMessage]); // Removed playerData dependency to prevent infinite loops

  // Update performPlayerAction to handle repair and upgrade actions
  const performPlayerActionWithBuildings = useCallback((actionKey, payload = {}) => {
    // Handle building-specific actions
    if (actionKey === 'repairItem' || actionKey === 'upgradeItem') {
      handleBuildingAction(actionKey, payload);
      return;
    }
    
    // Handle crafting control actions
    if (actionKey === 'stopCrafting') {
      const buildingKey = payload.buildingKey;
      if (buildingKey && playerData?.buildings?.[buildingKey]?.activeCrafting) {
        setPlayerData(prevData => {
          const newBuildings = { ...prevData.buildings };
          delete newBuildings[buildingKey].activeCrafting;
          return { ...prevData, buildings: newBuildings };
        });
        addMessage(`Stopped crafting in ${buildingKey}`, 'info');
        return;
      }
    }
    
    if (actionKey === 'toggleContinuousCrafting') {
      const buildingKey = payload.buildingKey;
      if (buildingKey && playerData?.buildings?.[buildingKey]?.activeCrafting) {
        setPlayerData(prevData => {
          const newBuildings = { ...prevData.buildings };
          const crafting = newBuildings[buildingKey].activeCrafting;
          newBuildings[buildingKey].activeCrafting = {
            ...crafting,
            continuousCrafting: !crafting.continuousCrafting
          };
          return { ...prevData, buildings: newBuildings };
        });
        const isEnabled = !playerData.buildings[buildingKey].activeCrafting.continuousCrafting;
        addMessage(`${isEnabled ? 'Enabled' : 'Disabled'} continuous crafting in ${buildingKey}`, 'info');
        return;
      }
    }
    
    // For all other actions, use the regular performPlayerAction
    performPlayerAction(actionKey, payload);
  }, [performPlayerAction, handleBuildingAction, playerData?.buildings, addMessage]);


  const goToLocation = useCallback((locationToGo, actionKey) => {
    if (!playerData || showEndOfDayModal || isProcessingEndOfDay) return;
    const actionConfig = gameActionsConfig[actionKey];
    if (!actionConfig) { addMessage(`Travel action ${actionKey} not found.`, 'error'); return; }
    if (playerData.currentLocation === locationToGo && !['returnHome', 'leaveShop'].includes(actionKey)) return;
    if (playerData.energy.current < actionConfig.energyCost) { addMessage("Not enough energy to travel!", 'warning'); return; }
    
    // Close mobile menu when navigating
    closeMobileMenu();
    let newInventory = { ...playerData.inventory };
    let newTripInventory = { ...playerData.tripInventory };
    let newCarryWeight = playerData.currentCarryWeight;
    let newEnergyCurrent = playerData.energy.current - actionConfig.energyCost;
    let newSkills = { ...playerData.skills };
    if (actionConfig.xpGain && actionConfig.xpGain.exploration) {
        if (newSkills.exploration) {
            newSkills.exploration = updateSkillXP(newSkills.exploration, actionConfig.xpGain.exploration);
        }
    }
    if (actionKey === 'returnHome' || actionKey === 'leaveShop') {
        addMessage("Returning home, transferring trip haul...", 'info');
        let itemsTransferredLog = [];
        for (const [itemKeyOrId, quantityOrInstance] of Object.entries(playerData.tripInventory)) {
            if (typeof quantityOrInstance === 'number' && quantityOrInstance > 0) { 
                const itemDetails = ItemData[itemKeyOrId];
                if (itemDetails) {
                    newInventory[itemKeyOrId] = (newInventory[itemKeyOrId] || 0) + quantityOrInstance;
                    itemsTransferredLog.push(`${itemDetails.name} x${quantityOrInstance}`);
                }
            } else if (typeof quantityOrInstance === 'object' && quantityOrInstance !== null && quantityOrInstance.id) { 
                const baseItemDetails = ItemData[quantityOrInstance.key];
                if (baseItemDetails) {
                    newInventory[quantityOrInstance.id] = quantityOrInstance; 
                    itemsTransferredLog.push(`${baseItemDetails.name} (1)`);
                }
            }
        }
        if (itemsTransferredLog.length > 0) addMessage(`Added to inventory: ${itemsTransferredLog.join(', ')}.`, "success");
        else if (Object.keys(playerData.tripInventory).length > 0) addMessage("No items to transfer from trip.", "info");
        newTripInventory = {};
        newCarryWeight = 0;
    }
    setPlayerData(prev => ({
    ...prev,
    energy: { ...prev.energy, current: newEnergyCurrent },
    currentLocation: locationToGo,
    inventory: newInventory,
    tripInventory: newTripInventory,
    currentCarryWeight: newCarryWeight,
    skills: newSkills,
    }));
    passTimeAndUpdateState(actionConfig.timeCost);
    addMessage(`Traveled to ${locationToGo}.`, 'info');
  }, [playerData, addMessage, passTimeAndUpdateState, showEndOfDayModal, isProcessingEndOfDay, closeMobileMenu]);

  const handleEquipItem = useCallback((itemIdToEquip) => {
    addMessage(`Equipping item: ${itemIdToEquip}`, 'info');
    setPlayerData(prevData => {
        if (!prevData) return prevData;
        const itemToEquip = prevData.inventory[itemIdToEquip];
        if (!itemToEquip || typeof itemToEquip !== 'object') { addMessage("Invalid item to equip.", 'error'); return prevData; }
        const baseItem = ItemData[itemToEquip.key];
        if (!baseItem || !baseItem.slotType || !prevData.equipment.hasOwnProperty(baseItem.slotType)) {
            addMessage(`${baseItem.name} cannot be equipped or slot is invalid.`, 'warning'); return prevData;
        }
        if (itemToEquip.durability !== null && itemToEquip.durability <= 0) {
            addMessage(`${baseItem.name} is broken.`, 'warning'); return prevData;
        }
        let newEquipment = { ...prevData.equipment };
        const targetSlot = baseItem.slotType;
        if (targetSlot === 'mainHand' && baseItem.handedness === 'twoHanded' && newEquipment.offHand) {
            const offHandItem = prevData.inventory[newEquipment.offHand];
            addMessage(`Unequipped ${ItemData[offHandItem?.key]?.name || 'Off-Hand'} (2H weapon).`, 'info');
            newEquipment.offHand = null;
        }
        if (targetSlot === 'offHand' && newEquipment.mainHand) {
            const mainHandItem = prevData.inventory[newEquipment.mainHand];
            if (mainHandItem && ItemData[mainHandItem.key]?.handedness === 'twoHanded') {
                addMessage(`Cannot equip ${baseItem.name}: Main hand is two-handed.`, 'warning'); return prevData;
            }
        }
        const currentlyEquipped = newEquipment[targetSlot];
        if (currentlyEquipped === itemIdToEquip) {
            newEquipment[targetSlot] = null; addMessage(`Unequipped ${baseItem.name}.`, 'success');
        } else {
            if (currentlyEquipped) {
                const oldItem = prevData.inventory[currentlyEquipped];
                addMessage(`Unequipped ${ItemData[oldItem?.key]?.name || 'previous item'}.`, 'info');
            }
            newEquipment[targetSlot] = itemIdToEquip; addMessage(`Equipped ${baseItem.name}.`, 'success');
        }
        return { ...prevData, equipment: newEquipment };
    });
  }, [addMessage]);

  const handleUnequipItem = useCallback((slotName) => {
    setPlayerData(prevData => {
        if (!prevData) return prevData;
        const equippedItemId = prevData.equipment[slotName];
        if (!equippedItemId) { addMessage("Nothing to unequip.", 'info'); return prevData; }
        const item = prevData.inventory[equippedItemId];
        addMessage(`Unequipped ${ItemData[item?.key]?.name || 'item'} from ${slotName}.`, 'success');
        return { ...prevData, equipment: { ...prevData.equipment, [slotName]: null } };
    });
  }, [addMessage]);

  const handleUseItem = useCallback((itemIdOrKey) => {
    setPlayerData(prevData => {
        if (!prevData) return prevData;
        let itemKey = itemIdOrKey;
        let itemInstanceOrCount = prevData.inventory[itemIdOrKey];
        let isStackableItem = typeof itemInstanceOrCount === 'number';
        if (!isStackableItem && typeof itemInstanceOrCount === 'object' && itemInstanceOrCount !== null) itemKey = itemInstanceOrCount.key;
        else if (!isStackableItem && itemInstanceOrCount === undefined) { itemInstanceOrCount = prevData.inventory[itemKey]; isStackableItem = typeof itemInstanceOrCount === 'number';}
        const baseItemData = ItemData[itemKey];
        if (!baseItemData || baseItemData.type !== 'consumable') { addMessage("Not consumable.", 'warning'); return prevData; }
        if (isStackableItem && (!itemInstanceOrCount || itemInstanceOrCount <= 0)) { addMessage(`No ${baseItemData.name} left.`, 'warning'); return prevData; }
        if (!isStackableItem && !prevData.inventory[itemIdOrKey]) { addMessage(`Item not found.`, 'warning'); return prevData; }
        let newPlayerData = { ...prevData }; let newInventory = { ...newPlayerData.inventory };
        let effectsApplied = false; let effectMessages = [];
        if (baseItemData.effects) {
            for (const [effect, value] of Object.entries(baseItemData.effects)) {
                switch (effect) {
                    case 'health': const nH = Math.min(newPlayerData.health.max, newPlayerData.health.current + value); if (nH > newPlayerData.health.current) { newPlayerData.health = { ...newPlayerData.health, current: nH }; newPlayerData.stats.health.current = nH; effectMessages.push(`+${value}H`); effectsApplied = true; } break;
                    case 'energy': const nE = Math.min(newPlayerData.energy.max, newPlayerData.energy.current + value); if (nE > newPlayerData.energy.current) { newPlayerData.energy = { ...newPlayerData.energy, current: nE }; effectMessages.push(`+${value}E`); effectsApplied = true; } break;
                    case 'mana': const nM = Math.min(newPlayerData.mana.max, newPlayerData.mana.current + value); if (nM > newPlayerData.mana.current) { newPlayerData.mana = { ...newPlayerData.mana, current: nM }; effectMessages.push(`+${value}M`); effectsApplied = true; } break;
                    case 'teleport': if (value === 'home' && newPlayerData.currentLocation !== 'home') { newPlayerData.currentLocation = 'home'; let log = []; for (const [k, q] of Object.entries(prevData.tripInventory)) { if (ItemData[k]?.stackable && q > 0) { newInventory[k] = (newInventory[k] || 0) + q; log.push(`${ItemData[k].name} x${q}`);}} if (log.length > 0) addMessage(`Trip haul: ${log.join(', ')}.`, "success"); newPlayerData.tripInventory = {}; newPlayerData.currentCarryWeight = 0; effectMessages.push(`Teleported Home`); effectsApplied = true; } else if (value === 'home') effectMessages.push(`Already home`); break;
                    default: console.warn(`Unknown effect: ${effect}`);
                }
            }
        }
        if (effectsApplied) {
            if (isStackableItem) { newInventory[itemKey] = itemInstanceOrCount - 1; if (newInventory[itemKey] <= 0) delete newInventory[itemKey]; }
            else delete newInventory[itemIdOrKey]; 
            newPlayerData.inventory = newInventory; addMessage(`Used ${baseItemData.name}. ${effectMessages.join(', ')}.`, 'success'); 
            
            // Force save after using items to prevent data loss
            setTimeout(() => {
              saveGameData(true);
            }, 100);
            
            return newPlayerData;
        } else { addMessage(`${baseItemData.name} had no effect.`, 'info'); return prevData; }
    });
  }, [addMessage, saveGameData]);

  const handleBuyItem = useCallback((itemKeyToBuy) => {
    if (!playerData) return;
    const baseItemData = ItemData[itemKeyToBuy];
    if (!baseItemData) { addMessage("Item not available.", 'error'); return; }
    const cost = baseItemData.shopPriceOverride !== undefined ? baseItemData.shopPriceOverride : (baseItemData.goldValue || 0);
    if (playerData.gold < cost) { addMessage(`Not enough gold for ${baseItemData.name}.`, 'warning'); return; }
    if (baseItemData.type === 'building' && playerData.buildings[itemKeyToBuy]?.owned) { addMessage(`You already own ${baseItemData.name}.`, 'info'); return; }
    
    // Check consumable purchase limits
    const consumableLimits = {
      'healingPotion': 1,
      'energyPotion': 1,
      'manaPotion': 1,
      'bandage': 3,
      'apple': 5,
      'berries': 5
    };
    
    if (baseItemData.type === 'consumable' && consumableLimits[itemKeyToBuy]) {
      const consumableTracking = ensureDailyConsumableTrackingReset(playerData);
      const currentPurchases = consumableTracking[itemKeyToBuy] || 0;
      const limit = consumableLimits[itemKeyToBuy];
      
      if (currentPurchases >= limit) {
        addMessage(`Daily limit reached for ${baseItemData.name} (${limit} per day).`, 'warning');
        return;
      }
    }
    let canAffordMaterials = true;
    if (baseItemData.materials) {
        for (const matKey in baseItemData.materials) {
            const needed = baseItemData.materials[matKey];
            if ((playerData.inventory[matKey] || 0) < needed) { addMessage(`Need ${needed - (playerData.inventory[matKey] || 0)} more ${ItemData[matKey].name} for ${baseItemData.name}.`, 'warning'); canAffordMaterials = false; break; }
        }
    }
    if (!canAffordMaterials) return;
    setPlayerData(prevData => {
        const newInventory = { ...prevData.inventory }; let newGold = prevData.gold - cost; let newBuildings = { ...prevData.buildings };
        if (baseItemData.materials) {
            for (const matKey in baseItemData.materials) { newInventory[matKey] = (newInventory[matKey] || 0) - baseItemData.materials[matKey]; if (newInventory[matKey] <= 0) delete newInventory[matKey]; }
        }
        
        // Track purchase achievements
        let updatedPlayerStats = { ...prevData.playerStats };
        let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
        
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldSpent', cost);
        
        // Update consumable purchase tracking
        let updatedConsumableTracking = ensureDailyConsumableTrackingReset(prevData);
        if (baseItemData.type === 'consumable' && updatedConsumableTracking.hasOwnProperty(itemKeyToBuy)) {
          updatedConsumableTracking = {
            ...updatedConsumableTracking,
            [itemKeyToBuy]: (updatedConsumableTracking[itemKeyToBuy] || 0) + 1
          };
        }
        
        if (baseItemData.type === 'building') { 
            newBuildings[itemKeyToBuy] = { ...(newBuildings[itemKeyToBuy] || { upgradeLevel: 0 }), owned: true }; 
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'buildingsPurchased', 1);
            addMessage(`Purchased ${baseItemData.name}!`, 'success'); 
        }
        else if (baseItemData.stackable) { 
            newInventory[itemKeyToBuy] = (newInventory[itemKeyToBuy] || 0) + 1; 
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsBought', 1);
            addMessage(`Bought 1 ${baseItemData.name}.`, 'success'); 
        }
        else { 
            const ni = createInitialItemInstance(itemKeyToBuy); 
            if (ni) newInventory[ni.id] = ni; 
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsBought', 1);
            addMessage(`Bought 1 ${baseItemData.name}.`, 'success'); 
        }
        
        const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
        if (newAchievements.length > 0) {
          updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
          newAchievements.forEach(achievement => {
            showAchievementNotification(achievement);
          });
        }
        
        return { 
            ...prevData, 
            gold: newGold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0), 
            inventory: newInventory, 
            buildings: newBuildings,
            playerStats: updatedPlayerStats,
            completedAchievements: updatedCompletedAchievements,
            dailyConsumablePurchases: updatedConsumableTracking
        };
    });
    
    // Force save after purchasing items to prevent data loss
    setTimeout(() => {
      saveGameData(true);
    }, 100);
  }, [playerData, addMessage, saveGameData]);

  const handleUnlockSeed = useCallback((seedKey) => {
    if (!playerData) return;
    
    const seedData = ItemData[seedKey];
    if (!seedData || seedData.category !== 'seed') {
      addMessage("Invalid seed to unlock.", 'error');
      return;
    }
    
    const seedUnlockPrices = {
      'carrotSeed': 50,
      'cucumberSeed': 75,
      'beetSeed': 100,
      'onionSeed': 125,
      'garlicSeed': 150,
      'kaleSeed': 175,
      'greenbeanSeed': 200,
      'tomatoSeed': 250,
      'pepperSeed': 300,
      'eggplantSeed': 350,
      'cauliflowerSeed': 400,
      'pumpkinSeed': 500,
    };
    
    const cost = seedUnlockPrices[seedKey];
    if (!cost) {
      addMessage("Seed unlock price not found.", 'error');
      return;
    }
    
    if (playerData.gold < cost) {
      addMessage(`Not enough gold to unlock ${seedData.name}. (${cost}G required)`, 'warning');
      return;
    }
    
    if (playerData.unlockedSeeds?.includes(seedKey)) {
      addMessage(`${seedData.name} is already unlocked!`, 'info');
      return;
    }
    
    setPlayerData(prevData => {
      const newUnlockedSeeds = [...(prevData.unlockedSeeds || []), seedKey];
      
      addMessage(`${seedData.name} unlocked! You can now find it while foraging.`, 'success');
      
      return {
        ...prevData,
        gold: prevData.gold - cost,
        unlockedSeeds: newUnlockedSeeds
      };
    });
  }, [playerData, addMessage]);

  // Helper functions for item locking
  const handleToggleItemLock = useCallback((itemIdOrKey) => {
    if (!playerData) return;
    
    setPlayerData(prevData => {
      const newLockedItems = { ...prevData.lockedItems };
      const isCurrentlyLocked = newLockedItems[itemIdOrKey];
      
      if (isCurrentlyLocked) {
        delete newLockedItems[itemIdOrKey];
        addMessage("Item unlocked.", 'info');
      } else {
        newLockedItems[itemIdOrKey] = true;
        addMessage("Item locked from selling.", 'info');
      }
      
      return {
        ...prevData,
        lockedItems: newLockedItems
      };
    });
    
    // Force save after locking/unlocking items to prevent data loss
    setTimeout(() => {
      saveGameData(true);
    }, 100);
  }, [playerData, addMessage, saveGameData]);

  const isItemLocked = useCallback((itemIdOrKey) => {
    return !!(playerData?.lockedItems?.[itemIdOrKey]);
  }, [playerData]);

  // Add sell all unlocked items functionality
  const handleSellAllUnlockedItems = useCallback(() => {
    if (!playerData) return;
    
    let totalGoldEarned = 0;
    let itemsSold = [];
    const newInventory = { ...playerData.inventory };
    const newLockedItems = { ...playerData.lockedItems };
    
    // Go through all inventory items
    for (const [itemIdOrKey, itemDataOrCount] of Object.entries(playerData.inventory)) {
      // Skip if item is locked
      if (newLockedItems[itemIdOrKey]) continue;
      
      // Skip if item is equipped
      if (Object.values(playerData.equipment).includes(itemIdOrKey)) continue;
      
      if (typeof itemDataOrCount === 'object' && itemDataOrCount !== null) {
        // Non-stackable item
        const baseItem = ItemData[itemDataOrCount.key];
        if (baseItem && baseItem.goldValue > 0) {
          totalGoldEarned += baseItem.goldValue;
          itemsSold.push(`${baseItem.name}`);
          delete newInventory[itemIdOrKey];
        }
      } else if (typeof itemDataOrCount === 'number' && itemDataOrCount > 0) {
        // Stackable item
        const baseItem = ItemData[itemIdOrKey];
        if (baseItem && baseItem.goldValue > 0) {
          const sellValue = baseItem.goldValue * itemDataOrCount;
          totalGoldEarned += sellValue;
          itemsSold.push(`${baseItem.name} x${itemDataOrCount}`);
          delete newInventory[itemIdOrKey];
        }
      }
    }
    
    if (totalGoldEarned > 0) {
      setPlayerData(prevData => ({
        ...prevData,
        gold: prevData.gold + totalGoldEarned,
        inventory: newInventory
      }));
      
      addMessage(`Sold all unlocked items for ${totalGoldEarned}G. Items: ${itemsSold.join(', ')}.`, 'success');
      
      // Force save after selling items to prevent data loss
      setTimeout(() => {
        saveGameData(true);
      }, 100);
    } else {
      addMessage("No unlocked items with value found to sell.", 'info');
    }
  }, [playerData, addMessage, saveGameData]);

  const handleSellItem_Robust = useCallback((itemIdOrKey, sellAll = false) => {
    if (!playerData) return;
    
    // Check if item is locked
    if (playerData.lockedItems?.[itemIdOrKey]) {
      addMessage("Cannot sell locked item. Unlock it first.", 'warning');
      return;
    }
    
    let itemKey, itemName, quantityToSell, goldEarned, isUniqueInstance, itemDataForBuyBack, uniqueIdForSale = null;
    let saleShouldProceed = false;
    const currentItemInstance = playerData.inventory[itemIdOrKey];
    if (typeof currentItemInstance === 'object' && currentItemInstance !== null) {
        isUniqueInstance = true; itemKey = currentItemInstance.key;
        const baseItem = ItemData[itemKey];
        if (!baseItem) { addMessage("Invalid item.", "error"); return; }
        if (Object.values(playerData.equipment).includes(itemIdOrKey)) { addMessage(`Cannot sell ${baseItem.name} while equipped.`, "warning"); return; }
        itemName = baseItem.name; const valuePerUnit = baseItem.goldValue || 0; quantityToSell = 1; goldEarned = valuePerUnit * quantityToSell;
        itemDataForBuyBack = { ...currentItemInstance }; uniqueIdForSale = itemIdOrKey;
        if (valuePerUnit > 0 || sellAll) saleShouldProceed = true; else addMessage(`${itemName} is worthless.`, "info");
    } else if (typeof currentItemInstance === 'number' && currentItemInstance > 0) {
        isUniqueInstance = false; itemKey = itemIdOrKey;
        const baseItem = ItemData[itemKey];
        if (!baseItem) { addMessage("Invalid item.", "error"); return; }
        itemName = baseItem.name; const valuePerUnit = baseItem.goldValue || 0; quantityToSell = sellAll ? currentItemInstance : 1;
        if (quantityToSell === 0) return;
        goldEarned = valuePerUnit * quantityToSell; itemDataForBuyBack = { ...baseItem, key: itemKey };
        if (valuePerUnit > 0 || sellAll) saleShouldProceed = true; else addMessage(`${itemName} is worthless.`, "info");
    } else { addMessage("Item not found/invalid quantity.", "warning"); return; }
    if (!saleShouldProceed) return;
    setPlayerData(prevData => {
        const newInventory = { ...prevData.inventory };
        if (isUniqueInstance) delete newInventory[uniqueIdForSale];
        else { newInventory[itemKey] -= quantityToSell; if (newInventory[itemKey] <= 0) delete newInventory[itemKey]; }
        
        // Track selling achievements
        let updatedPlayerStats = { ...prevData.playerStats };
        let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
        
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsSold', quantityToSell);
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldEarned', goldEarned);
        
        const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
        if (newAchievements.length > 0) {
          updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
          newAchievements.forEach(achievement => {
            showAchievementNotification(achievement);
          });
        }
        
        return { 
            ...prevData, 
            gold: prevData.gold + goldEarned + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0), 
            inventory: newInventory,
            playerStats: updatedPlayerStats,
            completedAchievements: updatedCompletedAchievements
        };
    });
    const newBuyBackEntry = { itemKeyOrId: itemKey, id: isUniqueInstance ? uniqueIdForSale : null, name: itemName, quantity: quantityToSell, durability: isUniqueInstance ? itemDataForBuyBack.durability : null, maxDurability: isUniqueInstance ? itemDataForBuyBack.maxDurability : null, isUnique: isUniqueInstance, originalValue: goldEarned, itemData: JSON.parse(JSON.stringify(itemDataForBuyBack)) };
    setBuyBackList(prevBuyBack => [newBuyBackEntry, ...prevBuyBack].slice(0, MAX_BUY_BACK_SLOTS));
    addMessage(`Sold ${quantityToSell} ${itemName} for ${goldEarned} G.`, 'success');
    
    // Force save after selling items to prevent data loss
    setTimeout(() => {
      saveGameData(true);
    }, 100);
  }, [playerData, addMessage, saveGameData]);

  const handleBuyBackItem = useCallback((buyBackIndex) => {
    if (!playerData) return;
    const itemEntry = buyBackList[buyBackIndex];
    if (!itemEntry) { addMessage("Invalid buy back selection.", "error"); return; }
    if (playerData.gold < itemEntry.originalValue) { addMessage("Not enough gold.", "warning"); return; }
    let successfulBuyBack = false;
    setPlayerData(prevData => {
        const newInventory = { ...prevData.inventory }; const newGold = prevData.gold - itemEntry.originalValue;
        if (itemEntry.isUnique && itemEntry.itemData && itemEntry.id) { newInventory[itemEntry.id] = JSON.parse(JSON.stringify(itemEntry.itemData)); successfulBuyBack = true; }
        else if (!itemEntry.isUnique && itemEntry.itemData) { newInventory[itemEntry.itemKeyOrId] = (newInventory[itemEntry.itemKeyOrId] || 0) + itemEntry.quantity; successfulBuyBack = true; }
        else { addMessage("Error restoring item data.", "error"); return prevData; }
        if (successfulBuyBack) addMessage(`Bought back ${itemEntry.name}.`, 'success');
        return { ...prevData, gold: newGold, inventory: newInventory };
    });
    if (successfulBuyBack) {
         setBuyBackList(prevList => prevList.filter((_, index) => index !== buyBackIndex));
    }
  }, [playerData?.gold, buyBackList, addMessage]);

  const handleUpgradeBuilding = useCallback((buildingKey) => {
    if (!playerData) return;
    const buildingData = playerData.buildings[buildingKey]; const upgradeConfig = gameConfigData.buildingUpgrades[buildingKey];
    if (!buildingData || !buildingData.owned) { addMessage("You don't own this.", "warning"); return; }
    if (!upgradeConfig) { addMessage("Cannot be upgraded.", "info"); return; }
    const currentLevel = buildingData.upgradeLevel;
    if (currentLevel >= upgradeConfig.maxLevel) { addMessage(`${ItemData[buildingKey].name} at max level.`, "info"); return; }
    const cost = upgradeConfig.costs[currentLevel + 1];
    if (playerData.gold < cost) { addMessage(`Need ${cost}G to upgrade.`, "warning"); return; }
    setPlayerData(prevData => {
        // Track building upgrade achievements
        let updatedPlayerStats = { ...prevData.playerStats };
        let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
        
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'buildingUpgrades', 1);
        updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldSpent', cost);
        
        const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
        if (newAchievements.length > 0) {
          updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
          newAchievements.forEach(achievement => {
            showAchievementNotification(achievement);
          });
        }
        
        return { 
            ...prevData, 
            gold: prevData.gold - cost + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0), 
            buildings: { ...prevData.buildings, [buildingKey]: { ...prevData.buildings[buildingKey], upgradeLevel: currentLevel + 1 } },
            playerStats: updatedPlayerStats,
            completedAchievements: updatedCompletedAchievements
        };
    });
    addMessage(`Upgraded ${ItemData[buildingKey].name} to Lvl ${currentLevel + 1}! (-${cost}G)`, "success");
  }, [playerData, addMessage]);

  const handleCompleteQuest = useCallback((questInstanceId) => {
    setPlayerData(prevData => {
        if (!prevData) return prevData;
        const quest = prevData.activeQuests.find(q => q.instanceId === questInstanceId);
        if (!quest) { addMessage("Quest not found.", "error"); return prevData; }
        let canComplete = false; let newInventory = { ...prevData.inventory };
        
        if (quest.type === 'kill') { 
            if ((quest.currentAmount || 0) >= quest.requiredAmount) canComplete = true; 
            else addMessage(`Not enough ${MonsterData[quest.target]?.name || quest.target} defeated for "${quest.title}".`, 'warning'); 
        }
        else if (quest.type === 'collect') { 
            if ((newInventory[quest.target] || 0) >= quest.requiredAmount) { 
                newInventory[quest.target] -= quest.requiredAmount; 
                if (newInventory[quest.target] <= 0) delete newInventory[quest.target]; 
                canComplete = true; 
            } else addMessage(`Missing ${quest.requiredAmount - (newInventory[quest.target] || 0)} ${ItemData[quest.target]?.name || quest.target} for "${quest.title}".`, 'warning'); 
        }
        else if (quest.type === 'mixed') {
            // Check if all requirements are met
            let allRequirementsMet = true;
            const missedRequirements = [];
            
            for (const req of quest.requirements) {
                if (req.type === 'kill') {
                    if ((req.currentAmount || 0) < req.amount) {
                        allRequirementsMet = false;
                        missedRequirements.push(`${req.amount - (req.currentAmount || 0)} more ${req.target} to defeat`);
                    }
                } else if (req.type === 'collect') {
                    if ((newInventory[req.target] || 0) < req.amount) {
                        allRequirementsMet = false;
                        missedRequirements.push(`${req.amount - (newInventory[req.target] || 0)} more ${ItemData[req.target]?.name || req.target} to collect`);
                    }
                }
            }
            
            if (allRequirementsMet) {
                // Consume collect items
                for (const req of quest.requirements) {
                    if (req.type === 'collect') {
                        newInventory[req.target] -= req.amount;
                        if (newInventory[req.target] <= 0) delete newInventory[req.target];
                    }
                }
                canComplete = true;
            } else {
                addMessage(`Quest "${quest.title}" incomplete: ${missedRequirements.join(', ')}.`, 'warning');
            }
        }
        else { addMessage(`Unknown quest type: "${quest.title}".`, 'error'); }
        
        if (canComplete) {
            const newGold = prevData.gold + quest.rewardGold; let newSkills = { ...prevData.skills };
            if (quest.xpGain) { for (const sid in quest.xpGain) if (newSkills[sid]) newSkills[sid] = updateSkillXP(newSkills[sid], quest.xpGain[sid]); }
            
            // Track quest completion achievements
            let updatedPlayerStats = { ...prevData.playerStats };
            let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
            
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'questsCompleted', 1);
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldEarned', quest.rewardGold);
            
            const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
            if (newAchievements.length > 0) {
              updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
              newAchievements.forEach(achievement => {
                showAchievementNotification(achievement);
              });
            }
            
            addMessage(`Quest "${quest.title}" complete! +${quest.rewardGold}G.`, 'success');
            return { 
                ...prevData, 
                gold: newGold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0), 
                inventory: newInventory, 
                skills: newSkills, 
                activeQuests: prevData.activeQuests.filter(q => q.instanceId !== questInstanceId),
                playerStats: updatedPlayerStats,
                completedAchievements: updatedCompletedAchievements
            };
        } return prevData;
    });
  }, [addMessage]);

  const handleFulfillOrder = useCallback((orderInstanceId) => {
    setPlayerData(prevData => {
        if (!prevData) return prevData;
        const order = prevData.customerOrders.find(o => o.instanceId === orderInstanceId);
        if (!order) { addMessage("Order not found.", "error"); return prevData; }
        let canFulfill = true; const tempInventory = { ...prevData.inventory }; const itemsToConsume = {};
        for (const [itemKey, requiredQuantity] of Object.entries(order.requestedItems)) {
            const baseItem = ItemData[itemKey]; if (!baseItem) { addMessage(`Unknown item ${itemKey}.`, "error"); canFulfill = false; break; }
            if (baseItem.stackable) { if ((tempInventory[itemKey] || 0) < requiredQuantity) { addMessage(`Not enough ${baseItem.name}. Need ${requiredQuantity}.`, 'warning'); canFulfill = false; break; } itemsToConsume[itemKey] = (itemsToConsume[itemKey] || 0) + requiredQuantity; }
            else { let found = 0; let ids = []; for (const id in tempInventory) { const item = tempInventory[id]; if (typeof item === 'object' && item.key === itemKey && !Object.values(prevData.equipment).includes(id)) { if (found < requiredQuantity) { ids.push(id); found++; } } } if (found < requiredQuantity) { addMessage(`Not enough ${baseItem.name}.`, 'warning'); canFulfill = false; break; } itemsToConsume[itemKey] = ids; }
        }
        if (canFulfill) {
            const newInventory = { ...prevData.inventory };
            for (const [itemKey, qtyOrIds] of Object.entries(itemsToConsume)) { if (Array.isArray(qtyOrIds)) qtyOrIds.forEach(id => delete newInventory[id]); else { newInventory[itemKey] -= qtyOrIds; if (newInventory[itemKey] <= 0) delete newInventory[itemKey]; } }
            const newGold = prevData.gold + order.rewardGold; let newSkills = { ...prevData.skills };
            if (order.xpGain) { for (const sid in order.xpGain) if (newSkills[sid]) newSkills[sid] = updateSkillXP(newSkills[sid], order.xpGain[sid]); }
            
            // Track order completion achievements
            let updatedPlayerStats = { ...prevData.playerStats };
            let updatedCompletedAchievements = [...(prevData.completedAchievements || [])];
            
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'ordersCompleted', 1);
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldEarned', order.rewardGold);
            
            const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
            if (newAchievements.length > 0) {
              updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
              newAchievements.forEach(achievement => {
                showAchievementNotification(achievement);
              });
            }
            
            addMessage(`Order for ${order.customerName} fulfilled! +${order.rewardGold}G.`, 'success');
            return { 
                ...prevData, 
                gold: newGold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0), 
                inventory: newInventory, 
                skills: newSkills, 
                customerOrders: prevData.customerOrders.filter(o => o.instanceId !== orderInstanceId),
                playerStats: updatedPlayerStats,
                completedAchievements: updatedCompletedAchievements
            };
        } return prevData;
    });
  }, [addMessage]);

  // Quest/Order Management Functions
  const ensureDailyTrackingReset = useCallback((playerData) => {
    const currentDay = playerData.currentDay || worldState.day;
    const tracking = playerData.dailyQuestOrderManagement || { 
      questsPurchased: 0, 
      ordersPurchased: 0, 
      lastResetDay: currentDay 
    };
    
    // Reset if it's a new day
    if (tracking.lastResetDay !== currentDay) {
      return {
        questsPurchased: 0,
        ordersPurchased: 0,
        lastResetDay: currentDay
      };
    }
    
    return tracking;
  }, [worldState.day]);

  const ensureDailyConsumableTrackingReset = useCallback((playerData) => {
    const currentDay = playerData.currentDay || worldState.day;
    const tracking = playerData.dailyConsumablePurchases || { 
      healingPotion: 0,
      energyPotion: 0,
      manaPotion: 0,
      bandage: 0,
      apple: 0,
      berries: 0,
      lastResetDay: currentDay 
    };
    
    // Reset if it's a new day
    if (tracking.lastResetDay !== currentDay) {
      return {
        healingPotion: 0,
        energyPotion: 0,
        manaPotion: 0,
        bandage: 0,
        apple: 0,
        berries: 0,
        lastResetDay: currentDay
      };
    }
    
    return tracking;
  }, [worldState.day]);

  const handleSkipQuest = useCallback((questInstanceId) => {
    const skipCost = 50;
    setPlayerData(prevData => {
      if (!prevData || prevData.gold < skipCost) {
        addMessage(`Not enough gold to skip quest (${skipCost}G required).`, 'warning');
        return prevData;
      }
      
      const quest = prevData.activeQuests.find(q => q.instanceId === questInstanceId);
      if (!quest) {
        addMessage("Quest not found.", "error");
        return prevData;
      }
      
      addMessage(`Skipped quest "${quest.title}" for ${skipCost}G.`, 'neutral');
      return {
        ...prevData,
        gold: prevData.gold - skipCost,
        activeQuests: prevData.activeQuests.filter(q => q.instanceId !== questInstanceId)
      };
    });
  }, [addMessage]);

  const handleSkipOrder = useCallback((orderInstanceId) => {
    const skipCost = 50;
    setPlayerData(prevData => {
      if (!prevData || prevData.gold < skipCost) {
        addMessage(`Not enough gold to skip order (${skipCost}G required).`, 'warning');
        return prevData;
      }
      
      const order = prevData.customerOrders.find(o => o.instanceId === orderInstanceId);
      if (!order) {
        addMessage("Order not found.", "error");
        return prevData;
      }
      
      addMessage(`Skipped order from ${order.customerName} for ${skipCost}G.`, 'neutral');
      return {
        ...prevData,
        gold: prevData.gold - skipCost,
        customerOrders: prevData.customerOrders.filter(o => o.instanceId !== orderInstanceId)
      };
    });
  }, [addMessage]);

  const handlePurchaseNewQuest = useCallback(() => {
    setPlayerData(prevData => {
      if (!prevData || !prevData.buildings?.questBoard?.owned) {
        addMessage("Quest Board required to purchase quests.", "warning");
        return prevData;
      }
      
      if (prevData.activeQuests.length >= gameConfigData.MAX_ACTIVE_QUESTS) {
        addMessage("Maximum active quests reached.", "warning");
        return prevData;
      }
      
      const dailyTracking = ensureDailyTrackingReset(prevData);
      const baseCost = 100;
      const cost = baseCost + (dailyTracking.questsPurchased * baseCost * 0.5);
      
      if (prevData.gold < cost) {
        addMessage(`Not enough gold to purchase quest (${cost}G required).`, 'warning');
        return prevData;
      }
      
      const availableQuestDefs = questDefinitions.filter(
        qDef => !prevData.activeQuests.some(aq => aq.templateId === qDef.id)
      );
      
      if (availableQuestDefs.length === 0) {
        addMessage("No new quests available.", "warning");
        return prevData;
      }
      
      const newQuestDef = availableQuestDefs[Math.floor(Math.random() * availableQuestDefs.length)];
      const newQuestInstance = {
        ...newQuestDef,
        instanceId: generateQuestInstanceId(),
        templateId: newQuestDef.id,
        currentAmount: 0,
      };
      
      if (newQuestDef.type === 'mixed' && newQuestDef.requirements) {
        newQuestInstance.requirements = newQuestDef.requirements.map(req => ({
          ...req,
          currentAmount: 0
        }));
      }
      
      addMessage(`Purchased new quest: "${newQuestInstance.title}" for ${cost}G.`, 'success');
      return {
        ...prevData,
        gold: prevData.gold - cost,
        activeQuests: [...prevData.activeQuests, newQuestInstance],
        dailyQuestOrderManagement: {
          ...dailyTracking,
          questsPurchased: dailyTracking.questsPurchased + 1
        }
      };
    });
  }, [addMessage, ensureDailyTrackingReset]);

  const handlePurchaseNewOrder = useCallback(() => {
    setPlayerData(prevData => {
      if (!prevData || !prevData.buildings?.shopFront?.owned) {
        addMessage("Shop Front required to purchase orders.", "warning");
        return prevData;
      }
      
      if (prevData.customerOrders.length >= gameConfigData.MAX_ACTIVE_ORDERS) {
        addMessage("Maximum customer orders reached.", "warning");
        return prevData;
      }
      
      const dailyTracking = ensureDailyTrackingReset(prevData);
      const baseCost = 150;
      const cost = baseCost + (dailyTracking.ordersPurchased * baseCost * 0.5);
      
      if (prevData.gold < cost) {
        addMessage(`Not enough gold to purchase order (${cost}G required).`, 'warning');
        return prevData;
      }
      
      const availableOrderDefs = customerOrderDefinitions.filter(
        orderDef => !prevData.customerOrders.some(ao => ao.templateId === orderDef.id)
      );
      
      if (availableOrderDefs.length === 0) {
        addMessage("No new orders available.", "warning");
        return prevData;
      }
      
      const newOrderDef = availableOrderDefs[Math.floor(Math.random() * availableOrderDefs.length)];
      const newOrderInstance = {
        ...newOrderDef,
        instanceId: generateOrderInstanceId(),
        templateId: newOrderDef.id,
      };
      
      addMessage(`New customer order from ${newOrderInstance.customerName} for ${cost}G.`, 'success');
      return {
        ...prevData,
        gold: prevData.gold - cost,
        customerOrders: [...prevData.customerOrders, newOrderInstance],
        dailyQuestOrderManagement: {
          ...dailyTracking,
          ordersPurchased: dailyTracking.ordersPurchased + 1
        }
      };
    });
  }, [addMessage, ensureDailyTrackingReset]);

  const handleFishingEnd = useCallback((result) => {
    setIsFishingMinigameActive(false);
    setWorldState(prev => ({ ...prev, isPaused: false }));
    if (result.outcome === 'caught' && result.caughtFishKey) {
        const fishKey = result.caughtFishKey;
        const fishData = ItemData[fishKey];
        if (fishData) {
            const catchCount = result.doubleCatch ? 2 : 1;
            addMessage(`You caught ${catchCount}x ${fishData.name}${result.doubleCatch ? ' (Double Catch!)' : ''}!`, 'success');
            setPlayerData(prev => {
                if (!prev) return prev;
                const newTripInventory = { ...prev.tripInventory };
                let totalWeight = 0;
                
                for (let i = 0; i < catchCount; i++) {
                    if (fishData.stackable) {
                        newTripInventory[fishKey] = (newTripInventory[fishKey] || 0) + 1;
                        totalWeight += (fishData.weight || 0.1);
                    } else { 
                        const newFishInstance = createInitialItemInstance(fishKey);
                        if (newFishInstance) {
                            newTripInventory[newFishInstance.id] = newFishInstance;
                            totalWeight += (fishData.weight || 0.1);
                        }
                    }
                }
                
                const newCarryWeight = prev.currentCarryWeight + totalWeight;
                if (newCarryWeight > prev.carryCapacity) {
                    addMessage(`Caught ${fishData.name}, but it's too heavy! Dropped.`, 'warning');
                    if (fishData.stackable) {
                        newTripInventory[fishKey] = Math.max(0, (newTripInventory[fishKey] || 0) - catchCount);
                        if (newTripInventory[fishKey] <= 0) delete newTripInventory[fishKey];
                    } 
                    return prev; 
                }
                
                // Calculate XP with tier and difficulty bonuses
                const baseFishingXP = gameActionsConfig.startFishing.xpGain?.fishing || 5;
                const tierBonus = Math.floor((fishData.fishingTier || 0) * 2);
                const difficultyMultiplier = {
                    'easy': 1,
                    'medium': 1.5,
                    'hard': 2,
                    'legendary': 3,
                    'junk': 0.5
                }[fishData.difficulty] || 1;
                
                const fishingXPGain = Math.floor((baseFishingXP + tierBonus) * difficultyMultiplier * catchCount);
                const newSkills = { ...prev.skills, fishing: updateSkillXP(prev.skills.fishing, fishingXPGain) };
                
                // Track fishing achievements
                let updatedPlayerStats = { ...prev.playerStats };
                let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
                
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'fishingSessionsCompleted', 1);
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'fishCaught', catchCount);
                
                const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
                if (newAchievements.length > 0) {
                  updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
                  newAchievements.forEach(achievement => {
                    showAchievementNotification(achievement);
                  });
                }
                
                return {
                    ...prev,
                    tripInventory: newTripInventory,
                    currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                    skills: newSkills,
                    playerStats: updatedPlayerStats,
                    completedAchievements: updatedCompletedAchievements,
                    gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
                };
            });
        } else {
            addMessage('Caught something unknown!', 'warning');
        }
    } else if (result.outcome === 'lost') {
        addMessage(`The fish got away... ${result.reason || ''}`, 'warning');
        // Still track the fishing session attempt
        setPlayerData(prev => {
            if (!prev) return prev;
            let updatedPlayerStats = { ...prev.playerStats };
            let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
            
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'fishingSessionsCompleted', 1);
            
            const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
            if (newAchievements.length > 0) {
              updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
              newAchievements.forEach(achievement => {
                showAchievementNotification(achievement);
              });
            }
            
            return {
                ...prev,
                playerStats: updatedPlayerStats,
                completedAchievements: updatedCompletedAchievements,
                gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
            };
        });
    } else if (result.outcome === 'cancelled' || result.outcome === 'closed') {
        addMessage('Stopped fishing.', 'info');
    }
  }, [addMessage]);

  const handleMiningEnd = useCallback((result) => {
    setIsMiningMinigameActive(false);
    setWorldState(prev => ({ ...prev, isPaused: false }));
    if (result.outcome === 'success' && result.clickedOres && result.clickedOres.length > 0) {
        console.log('[Layout] Mining success! Received clicked ores:', result.clickedOres.map(o => o.name));
        
        // Count up what was clicked
        const oreCounts = {};
        result.clickedOres.forEach(ore => {
            oreCounts[ore.key] = (oreCounts[ore.key] || 0) + 1;
        });
        
        console.log('[Layout] Ore counts calculated:', oreCounts);
        
        const oreNames = Object.keys(oreCounts).map(key => {
            const oreData = ItemData[key];
            const count = oreCounts[key];
            return oreData ? `${oreData.name} x${count}` : `${key} x${count}`;
        }).join(', ');
        
        addMessage(`You successfully mined ${oreNames}!`, 'success');
        
        setPlayerData(prev => {
            if (!prev) return prev;
            const newTripInventory = { ...prev.tripInventory };
            let totalWeight = 0;
            let itemsAdded = [];
            
            // Add the clicked ores to inventory
            for (const [oreKey, quantity] of Object.entries(oreCounts)) {
                const oreData = ItemData[oreKey];
                if (oreData && oreData.stackable) {
                    const currentAmount = newTripInventory[oreKey] || 0;
                    const newAmount = currentAmount + quantity;
                    console.log(`[Layout] Adding ${oreData.name}: ${currentAmount} + ${quantity} = ${newAmount}`);
                    newTripInventory[oreKey] = newAmount;
                    totalWeight += (oreData.weight || 0) * quantity;
                    itemsAdded.push(`${oreData.name} x${quantity}`);
                }
            }
            
            console.log('[Layout] Final trip inventory update:', newTripInventory);
            
            const newCarryWeight = prev.currentCarryWeight + totalWeight;
            if (newCarryWeight > prev.carryCapacity) {
                addMessage(`Mined successfully, but items are too heavy! Dropped.`, 'warning');
                return prev;
            }
            
            const miningXPGain = gameActionsConfig.mineOre.xpGain?.mining || 10;
            const newSkills = { ...prev.skills, mining: updateSkillXP(prev.skills.mining, miningXPGain) };
            
            // Track mining achievements
            let updatedPlayerStats = { ...prev.playerStats };
            let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
            
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'miningSessionsCompleted', 1);
            if (result.score && result.score === result.maxScore) {
              updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'perfectMiningScores', 1);
            }
            
            // Track specific ore types
            Object.keys(oreCounts).forEach(oreKey => {
              const oreData = ItemData[oreKey];
              if (oreData && oreData.name) {
                if (oreKey === 'copperOre') {
                  updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'copperOreMined', oreCounts[oreKey]);
                } else if (oreKey === 'ironOre') {
                  updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'ironOreMined', oreCounts[oreKey]);
                } else if (oreKey === 'goldOre') {
                  updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'goldOreMined', oreCounts[oreKey]);
                }
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'totalOreMined', oreCounts[oreKey]);
              }
            });
            
            const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
            if (newAchievements.length > 0) {
              updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
              newAchievements.forEach(achievement => {
                showAchievementNotification(achievement);
              });
            }
            
            return {
                ...prev,
                tripInventory: newTripInventory,
                currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                skills: newSkills,
                playerStats: updatedPlayerStats,
                completedAchievements: updatedCompletedAchievements,
                gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
            };
        });
    } else if (result.outcome === 'failed') {
        addMessage(`Mining failed... ${result.reason || ''}`, 'warning');
    } else if (result.outcome === 'cancelled') {
        addMessage('Stopped mining.', 'info');
    }
  }, [addMessage]);

  const processedForagingResultsRef = useRef(new Set());
  
  const handleForagingEnd = useCallback((result) => {
    // Create a unique identifier for this foraging session
    const sessionId = result.sessionId || `${result.outcome}_${result.score || 0}_${result.foundItems?.length || 0}_${Date.now()}`;
    
    // Check if we've already processed this result
    if (processedForagingResultsRef.current.has(sessionId)) {
      console.log(`[Layout] 🚫 Duplicate foraging result detected (${sessionId}), ignoring`);
      return;
    }
    
    // Mark this result as processed
    processedForagingResultsRef.current.add(sessionId);
    console.log(`[Layout] ✅ Processing foraging result (${sessionId})`);
    
    // Clean up old results to prevent memory leaks (keep only last 10)
    if (processedForagingResultsRef.current.size > 10) {
      const resultsArray = Array.from(processedForagingResultsRef.current);
      processedForagingResultsRef.current.clear();
      resultsArray.slice(-5).forEach(id => processedForagingResultsRef.current.add(id));
    }
    
    setIsForagingMinigameActive(false);
    setWorldState(prev => ({ ...prev, isPaused: false }));
    if (result.outcome === 'success' && result.foundItems && result.foundItems.length > 0) {
        console.log('[Layout] Foraging success! Received found items:', result.foundItems.map(i => `${i.name} (${i.key})`));
        
        // Count up what was found
        const itemCounts = {};
        result.foundItems.forEach(item => {
            itemCounts[item.key] = (itemCounts[item.key] || 0) + 1;
        });
        
        console.log('[Layout] 🎯 Item counts calculated:', itemCounts);
        
        const itemNames = Object.keys(itemCounts).map(key => {
            const itemData = ItemData[key];
            const count = itemCounts[key];
            return itemData ? `${itemData.name} x${count}` : `${key} x${count}`;
        }).join(', ');
        
        addMessage(`You successfully foraged ${itemNames}!`, 'success');
        
        setPlayerData(prev => {
            if (!prev) return prev;
            const newTripInventory = { ...prev.tripInventory };
            let totalWeight = 0;
            let itemsAdded = [];
            
            // Add the found items to inventory
            for (const [itemKey, quantity] of Object.entries(itemCounts)) {
                const itemData = ItemData[itemKey];
                if (itemData && itemData.stackable) {
                    const currentAmount = newTripInventory[itemKey] || 0;
                    const newAmount = currentAmount + quantity;
                    newTripInventory[itemKey] = newAmount;
                    totalWeight += (itemData.weight || 0) * quantity;
                    itemsAdded.push(`${itemData.name} x${quantity}`);
                }
            }
            
            console.log('[Layout] 📦 Added to trip inventory:', itemsAdded.join(', '));
            
            const newCarryWeight = prev.currentCarryWeight + totalWeight;
            if (newCarryWeight > prev.carryCapacity) {
                addMessage(`Foraged successfully, but items are too heavy! Dropped.`, 'warning');
                return prev;
            }
            
            const foragingXPGain = gameActionsConfig.forage.xpGain?.foraging || 5;
            const survivalXPGain = gameActionsConfig.forage.xpGain?.survival || 1;
            const herbloreXPGain = gameActionsConfig.forage.xpGain?.herblore || 2;
            const newSkills = { 
                ...prev.skills, 
                foraging: updateSkillXP(prev.skills.foraging, foragingXPGain),
                survival: updateSkillXP(prev.skills.survival, survivalXPGain),
                herblore: updateSkillXP(prev.skills.herblore, herbloreXPGain)
            };
            
            // Track foraging achievements
            let updatedPlayerStats = { ...prev.playerStats };
            let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
            
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'foragingSessionsCompleted', 1);
            if (result.score && result.score === result.maxScore) {
              updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'perfectForagingScores', 1);
            }
            
            // Track total items foraged
            const totalItemsFound = Object.values(itemCounts).reduce((sum, count) => sum + count, 0);
            updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'itemsForaged', totalItemsFound);
            
            const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
            if (newAchievements.length > 0) {
              updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
              newAchievements.forEach(achievement => {
                showAchievementNotification(achievement);
              });
            }
            
            return {
                ...prev,
                tripInventory: newTripInventory,
                currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                skills: newSkills,
                playerStats: updatedPlayerStats,
                completedAchievements: updatedCompletedAchievements,
                gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
            };
        });
    } else if (result.outcome === 'failed') {
        addMessage(`Foraging failed... ${result.reason || ''}`, 'warning');
    } else if (result.outcome === 'cancelled') {
        addMessage('Stopped foraging.', 'info');
    }
  }, [addMessage]);

  const handleWoodChoppingEnd = useCallback((result) => {
    setIsWoodChoppingMinigameActive(false);
    setWorldState(prev => ({ ...prev, isPaused: false }));
    
    if (result.outcome === 'success' && result.woodGathered > 0) {
        console.log('[Layout] Wood chopping success! Gathered wood:', result.woodGathered);
        addMessage(`Successfully chopped ${result.woodGathered} wood!`, 'success');
        
        setPlayerData(prev => {
            if (!prev) return prev;
            const newTripInventory = { ...prev.tripInventory };
            const woodData = ItemData['wood'];
            
            if (woodData && woodData.stackable) {
                const currentAmount = newTripInventory['wood'] || 0;
                const newAmount = currentAmount + result.woodGathered;
                newTripInventory['wood'] = newAmount;
                
                const totalWeight = (woodData.weight || 0) * result.woodGathered;
                const newCarryWeight = prev.currentCarryWeight + totalWeight;
                
                if (newCarryWeight > prev.carryCapacity) {
                    addMessage(`Chopped wood successfully, but it's too heavy! Dropped.`, 'warning');
                    return prev;
                }
                
                const woodcuttingXPGain = gameActionsConfig.chopWood.xpGain?.woodcutting || 8;
                const newSkills = { 
                    ...prev.skills, 
                    woodcutting: updateSkillXP(prev.skills.woodcutting, woodcuttingXPGain * result.woodGathered)
                };
                
                // Track wood chopping achievements
                let updatedPlayerStats = { ...prev.playerStats };
                let updatedCompletedAchievements = [...(prev.completedAchievements || [])];
                
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'woodchoppingSessionsCompleted', 1);
                updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'woodChopped', result.woodGathered);
                if (result.score && result.score === result.maxScore) {
                  updatedPlayerStats = updatePlayerStat(updatedPlayerStats, 'perfectWoodchoppingScores', 1);
                }
                
                const newAchievements = checkForNewAchievements(updatedPlayerStats, updatedCompletedAchievements);
                if (newAchievements.length > 0) {
                  updatedCompletedAchievements = [...updatedCompletedAchievements, ...newAchievements.map(ach => ach.id)];
                  newAchievements.forEach(achievement => {
                    showAchievementNotification(achievement);
                  });
                }
                
                return {
                    ...prev,
                    tripInventory: newTripInventory,
                    currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                    skills: newSkills,
                    playerStats: updatedPlayerStats,
                    completedAchievements: updatedCompletedAchievements,
                    gold: prev.gold + newAchievements.reduce((sum, ach) => sum + ach.goldReward, 0)
                };
            }
            
            return prev;
        });
    } else if (result.outcome === 'failed') {
        addMessage(`Wood chopping failed... ${result.reason || ''}`, 'warning');
    } else if (result.outcome === 'cancelled') {
        addMessage('Stopped chopping wood.', 'info');
    }
  }, [addMessage]);

  const handleFarmingEnd = useCallback((result) => {
    setIsFarmingMinigameActive(false);
    
    if (result.outcome === 'completed' && result.rewards) {
        console.log('[Layout] Farming success! Rewards:', result.rewards);
        
        // Add rewards to trip inventory
        const rewardMessages = [];
        let totalWeight = 0;
        
        setPlayerData(prev => {
            if (!prev) return prev;
            const newTripInventory = { ...prev.tripInventory };
            
            // Add the rewards to inventory
            Object.entries(result.rewards).forEach(([itemKey, quantity]) => {
                const itemData = ItemData[itemKey];
                if (itemData && itemData.stackable) {
                    const currentAmount = newTripInventory[itemKey] || 0;
                    const newAmount = currentAmount + quantity;
                    newTripInventory[itemKey] = newAmount;
                    totalWeight += (itemData.weight || 0) * quantity;
                    rewardMessages.push(`${itemData.name} x${quantity}`);
                }
            });
            
            const newCarryWeight = prev.currentCarryWeight + totalWeight;
            if (newCarryWeight > prev.carryCapacity) {
                addMessage(`Farming complete, but items are too heavy! Dropped.`, 'warning');
                return prev;
            }
            
            // Add farming XP if provided
            let newSkills = { ...prev.skills };
            if (result.xpGained && result.xpGained.farming) {
                newSkills.farming = updateSkillXP(newSkills.farming, result.xpGained.farming);
            }
            
            // Save farm state if provided
            let updatedData = {
                ...prev,
                tripInventory: newTripInventory,
                currentCarryWeight: parseFloat(newCarryWeight.toFixed(2)),
                skills: newSkills
            };
            
            if (result.farmState) {
                updatedData.farmState = result.farmState;
            }
            
            return updatedData;
        });
        
        const xpMessage = result.xpGained ? `+${result.xpGained.farming} Farming XP` : '';
        const harvestMessage = result.harvestedCrops ? `Harvested ${result.harvestedCrops} crops` : '';
        
        if (rewardMessages.length > 0) {
            addMessage(`Farming session complete! ${harvestMessage}. Rewards: ${rewardMessages.join(', ')}. ${xpMessage}`, 'success');
        } else {
            addMessage(`Farming session complete! ${harvestMessage}. ${xpMessage}`, 'success');
        }
    } else if (result.outcome === 'failed') {
        addMessage(`Farming failed... ${result.reason || ''}`, 'warning');
    } else if (result.outcome === 'cancelled' || result.outcome === 'closed') {
        addMessage('You leave your farm.', 'info');
    }
  }, [addMessage]);

  const handleFarmingEnergyUpdate = useCallback((update) => {
    setPlayerData(prev => {
      if (!prev) return prev;
      
      let updatedData = { ...prev };
      
      // Handle energy changes
      if (update.energyChange) {
        const newEnergy = Math.max(0, Math.min(prev.energy.max, prev.energy.current + update.energyChange));
        updatedData.energy = { ...prev.energy, current: newEnergy };
      }
      
      // Handle inventory changes
      if (update.inventoryChange) {
        const newInventory = { ...prev.inventory };
        Object.entries(update.inventoryChange).forEach(([itemKey, changeAmount]) => {
          const currentAmount = newInventory[itemKey] || 0;
          const newAmount = currentAmount + changeAmount;
          
          if (newAmount <= 0) {
            delete newInventory[itemKey];
          } else {
            newInventory[itemKey] = newAmount;
          }
        });
        updatedData.inventory = newInventory;
      }
      
      // Handle farm state updates
      if (update.farmState) {
        updatedData.farmState = update.farmState;
      }
      
      return updatedData;
    });
  }, []);

  const handleDungeonMiningEnd = useCallback((result) => {
    setIsDungeonMiningMinigameActive(false);
    // No need to unpause since we never paused time
    
    if (result.outcome === 'died') {
        addMessage(`💀 You died on floor ${result.floorsReached}! ${result.reason}`, 'error');
        return;
    }
    
    if (result.outcome === 'passed_out') {
        addMessage(`😵 You passed out on floor ${result.floorsReached}! ${result.reason}`, 'warning');
    }

    
    if ((result.outcome === 'success' || result.outcome === 'passed_out') && result.rewards) {
        console.log('[Layout] Dungeon mining success! Rewards:', result.rewards);
        
        // Add rewards to trip inventory (not main inventory)
        const rewardMessages = [];
        let totalMiningXP = 0;
        let totalCombatXP = 0;
        let totalExplorationXP = 0;
        
        setPlayerData(prev => {
            const newTripInventory = { ...prev.tripInventory };
            let newCarryWeight = prev.currentCarryWeight;
            
            Object.entries(result.rewards).forEach(([itemKey, quantity]) => {
                if (quantity > 0) {
                    const itemData = ItemData[itemKey];
                    const itemWeight = (itemData?.weight || 0.1) * quantity;
                    
                    // Check if adding this item would exceed carry capacity
                    if (newCarryWeight + itemWeight <= prev.carryCapacity) {
                        newTripInventory[itemKey] = (newTripInventory[itemKey] || 0) + quantity;
                        newCarryWeight += itemWeight;
                        const itemName = itemData?.name || itemKey;
                        rewardMessages.push(`${itemName} x${quantity}`);
                    } else {
                        // Item is too heavy, drop it
                        const itemName = itemData?.name || itemKey;
                        rewardMessages.push(`${itemName} x${quantity} (dropped - too heavy)`);
                    }
                    
                    // Calculate XP based on item type
                    if (itemKey.includes('Ore') || itemKey === 'stone' || itemKey.includes('Gem')) {
                        totalMiningXP += quantity * 8;
                    }
                    if (itemKey.includes('gold') || itemKey.includes('Gold')) {
                        totalExplorationXP += quantity * 2;
                    }
                }
            });
            
            // Add combat XP based on floors reached
            totalCombatXP = Math.floor(result.floorsReached * 5);
            totalExplorationXP += Math.floor(result.floorsReached * 3);
            
            // Update skills with XP
            const newSkills = { ...prev.skills };
            if (totalMiningXP > 0 && newSkills.mining) {
                newSkills.mining = updateSkillXP(newSkills.mining, totalMiningXP);
            }
            if (totalCombatXP > 0 && newSkills.combat) {
                newSkills.combat = updateSkillXP(newSkills.combat, totalCombatXP);
            }
            if (totalExplorationXP > 0 && newSkills.exploration) {
                newSkills.exploration = updateSkillXP(newSkills.exploration, totalExplorationXP);
            }
            
            return {
                ...prev,
                tripInventory: newTripInventory,
                currentCarryWeight: newCarryWeight,
                skills: newSkills,
                // Update health and energy based on final values
                health: { ...prev.health, current: result.finalHealth || prev.health.current },
                energy: { ...prev.energy, current: result.finalEnergy || prev.energy.current }
            };
        });
        
        const xpMessage = `+${totalMiningXP} Mining XP, +${totalCombatXP} Combat XP, +${totalExplorationXP} Exploration XP`;
        
        if (rewardMessages.length > 0) {
            addMessage(`Dungeon mining complete! Reached floor ${result.floorsReached}. Rewards: ${rewardMessages.join(', ')}. ${xpMessage}`, 'success');
        } else {
            addMessage(`Dungeon mining complete! Reached floor ${result.floorsReached}. ${xpMessage}`, 'success');
        }
    } else if (result.outcome === 'failed') {
        addMessage(`Dungeon mining failed... ${result.reason || 'You were defeated in the depths.'}`, 'warning');
    } else if (result.outcome === 'cancelled') {
        addMessage('You exit the dungeon mines.', 'info');
    }
  }, [addMessage]);

  const generateNewQuest = useCallback(() => {
    setPlayerData(prevData => {
        if (!prevData || !prevData.buildings) return prevData;
        if (!prevData.buildings.questBoard?.owned || prevData.activeQuests.length >= gameConfigData.MAX_ACTIVE_QUESTS) {
            return prevData;
        }
        const availableQuestDefs = questDefinitions.filter(
            qDef => !prevData.activeQuests.some(aq => aq.templateId === qDef.id)
        );
        if (availableQuestDefs.length === 0) {
            return prevData;
        }
        const newQuestDef = availableQuestDefs[Math.floor(Math.random() * availableQuestDefs.length)];
        const newQuestInstance = {
            ...newQuestDef,
            instanceId: generateQuestInstanceId(),
            templateId: newQuestDef.id,
            currentAmount: 0,
        };
        
        // Initialize mixed quest requirements
        if (newQuestDef.type === 'mixed' && newQuestDef.requirements) {
            newQuestInstance.requirements = newQuestDef.requirements.map(req => ({
                ...req,
                currentAmount: 0
            }));
        }
        addMessage(`New quest posted: "${newQuestInstance.title}"`, 'neutral');
        return { ...prevData, activeQuests: [...prevData.activeQuests, newQuestInstance] };
    });
  }, [addMessage]);

  const generateNewCustomerOrder = useCallback(() => {
    setPlayerData(prevData => {
        if (!prevData || !prevData.buildings?.shopFront?.owned) {
            return prevData;
        }
        if (!prevData.buildings.shopFront?.owned || prevData.customerOrders.length >= gameConfigData.MAX_ACTIVE_ORDERS) {
            return prevData;
        }
        const availableOrderDefs = customerOrderDefinitions.filter(
            oDef => !prevData.customerOrders.some(ao => ao.templateId === oDef.id)
        );
        if (availableOrderDefs.length === 0) {
            return prevData;
        }
        const newOrderDef = availableOrderDefs[Math.floor(Math.random() * availableOrderDefs.length)];
        const newOrderInstance = {
            ...newOrderDef,
            instanceId: generateOrderInstanceId(),
            templateId: newOrderDef.id,
        };
        addMessage(`New customer order from ${newOrderInstance.customerName}!`, 'neutral');
        return { ...prevData, customerOrders: [...prevData.customerOrders, newOrderInstance] };
    });
  }, [addMessage]);

  useEffect(() => {
    if (!playerData) return;
    const canGenerate = worldState.currentTimeInMinutes === 0 && !worldState.isPaused && !worldState._passedOut && !showEndOfDayModal && !isProcessingEndOfDay;
    if (canGenerate && playerData.buildings) {
      const timerId = setTimeout(() => {
        if (playerData.buildings.questBoard?.owned && playerData.activeQuests.length < gameConfigData.MAX_ACTIVE_QUESTS) {
            generateNewQuest();
        }
        if (playerData.buildings.shopFront?.owned && playerData.customerOrders.length < gameConfigData.MAX_ACTIVE_ORDERS) {
            generateNewCustomerOrder();
        }
      }, 0);
      return () => clearTimeout(timerId);
    }
  }, [
      worldState.currentTimeInMinutes, worldState.isPaused, worldState._passedOut, showEndOfDayModal, isProcessingEndOfDay,
      playerData, // Include playerData instead of specific properties
      generateNewQuest, generateNewCustomerOrder
    ]);

  useEffect(() => {
    if (worldState.isPaused || isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
        return;
    }
    const timerId = setInterval(() => passTimeAndUpdateState(MINUTES_PER_TICK), GAME_TICK_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, [worldState.isPaused, isCombatActive, isFishingMinigameActive, isMiningMinigameActive, isForagingMinigameActive, isWoodChoppingMinigameActive, isBossWaveActive, showEndOfDayModal, worldState._passedOut, isProcessingEndOfDay, passTimeAndUpdateState]);

  useEffect(() => {
    if (worldState._passedOut && !showEndOfDayModal && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isProcessingEndOfDay) {
        console.log("[Layout] _passedOut is true, triggering handleRestDay(true)");
        handleRestDay(true);
    }
  }, [worldState._passedOut, showEndOfDayModal, isCombatActive, isFishingMinigameActive, isMiningMinigameActive, isForagingMinigameActive, isWoodChoppingMinigameActive, isDungeonMiningMinigameActive, handleRestDay, isProcessingEndOfDay]);

  // Handle energy exhaustion - trigger automatic rest when energy reaches 0
  useEffect(() => {
    if (playerData && playerData.energy.current <= 0 && !worldState._passedOut && !showEndOfDayModal && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isProcessingEndOfDay) {
        console.log("[Layout] Energy depleted, triggering forced rest");
        handleRestDay(true);
    }
  }, [playerData, worldState._passedOut, showEndOfDayModal, isCombatActive, isFishingMinigameActive, isMiningMinigameActive, isForagingMinigameActive, isWoodChoppingMinigameActive, isDungeonMiningMinigameActive, isProcessingEndOfDay, handleRestDay]);

  if (!playerData || !playerData.stats || !playerData.health || !playerData.mana || !playerData.energy || !playerData.skills) {
    return <div style={{color: "white", textAlign: "center", paddingTop: "50px", fontSize: "1.2em"}}>Loading game data...</div>;
  }

  const renderMainPanels = () => {
    if (!playerData) return <div>Loading player data...</div>;

    if (playerData.currentLocation === 'wilderness') {
      return <WildernessView
                playerData={playerData}
                worldState={worldState}
                onPlayerAction={performPlayerAction}
             />;
    }
    if (playerData.currentLocation === 'shop') {
      return <ShopView
                playerData={playerData}
                onBuyItem={handleBuyItem}
                onUpgradeBuilding={handleUpgradeBuilding}
                buyBackList={buyBackList}
                onBuyBackItem={handleBuyBackItem}
             />;
    }
    switch (activeTab) {
      case 'overview':
        // Use HomeView when at home, CharacterAndInventoryView elsewhere
        if (playerData.currentLocation === 'home') {
          return <HomeView
                      playerData={playerData}
                      setPlayerData={setPlayerData}
                      onEquipItem={handleEquipItem}
                      onUnequipItem={handleUnequipItem}
                      onUseItem={handleUseItem}
                      onToggleItemLock={handleToggleItemLock}
                      isItemLocked={isItemLocked}
                      tooltipsEnabled={tooltipsEnabled}
                      onTabChange={setActiveTab}
                      worldState={worldState}
                      onRest={() => {
                          if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
                              addMessage("Cannot rest now.", "warning"); return;
                          }
                          if (playerData.currentLocation === 'home') {
                            handleRestDay(false);
                          } else {
                            addMessage("You must be at home to rest.", "warning");
                          }
                      }}
                      canRest={playerData.currentLocation === 'home' && !worldState.isPaused && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isBossWaveActive && !showEndOfDayModal && !worldState._passedOut && !isProcessingEndOfDay}
                      onLocationChange={(location) => {
                          if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
                              addMessage("Cannot travel now.", "warning"); return;
                          }
                          if (location === 'wilderness') {
                              goToLocation('wilderness', 'goToWilderness');
                          } else if (location === 'shop') {
                              goToLocation('shop', 'goToShop');
                          }
                      }}
                  />;
        } else {
          return <CharacterAndInventoryView
                      playerData={playerData}
                      setPlayerData={setPlayerData}
                      onEquipItem={handleEquipItem}
                      onUnequipItem={handleUnequipItem}
                      onUseItem={handleUseItem}
                      onToggleItemLock={handleToggleItemLock}
                      isItemLocked={isItemLocked}
                      tooltipsEnabled={tooltipsEnabled}
                  />;
        }
      case 'quests':
        return <QuestsPanel
                    playerData={playerData}
                    onCompleteQuest={handleCompleteQuest}
                    onFulfillOrder={handleFulfillOrder}
                    onSkipQuest={handleSkipQuest}
                    onSkipOrder={handleSkipOrder}
                    onPurchaseNewQuest={handlePurchaseNewQuest}
                    onPurchaseNewOrder={handlePurchaseNewOrder}
                />;
      case 'skills':
        return <SkillsPanel playerData={playerData} />;
      case 'achievements':
        return <AchievementsPanel playerData={playerData} playerStats={playerData?.playerStats || {}} />;
      
      // Building tabs
      case 'building-furnace':
        return <FurnaceView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-forge':
        return <ForgeView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-sawmill':
        return <SawmillView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-workbench':
        return <WorkbenchView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-anvil':
        return <AnvilView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-campfire':
        return <CampfireView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-brewingStation':
        return <BrewingStationView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-tanningStation':
        return <TanningStationView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-runecraftingStation':
        return <RunecraftingStationView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-landPlot':
        return <LandPlotView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onUnlockSeed={handleUnlockSeed}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-fletchingStation':
        return <FletchingStationView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
      case 'building-thievingDummy':
        return <ThievingDummyView
                    playerData={playerData}
                    onPlayerAction={performPlayerActionWithBuildings}
                    onGoBack={() => setActiveTab('overview')}
                />;
                
      default:
        return <CharacterAndInventoryView
                    playerData={playerData}
                    setPlayerData={setPlayerData}
                    onEquipItem={handleEquipItem}
                    onUnequipItem={handleUnequipItem}
                    onUseItem={handleUseItem}
                    tooltipsEnabled={tooltipsEnabled}
                />;
    }
  };

  return (
    <div className={styles.gameScreen}>
      {/* Mobile Overlay */}
      <div 
        className={`${styles.mobileOverlay} ${isMobileMenuVisible ? styles.mobileOverlayVisible : ''}`}
        onClick={closeMobileMenu}
      />
      
      <div className={`${styles.sidebar} ${isMobileMenuVisible ? styles.sidebarVisible : ''}`}>
        <Sidebar
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            closeMobileMenu(); // Close mobile menu when changing tabs
          }}
          playerData={playerData}
          tooltipsEnabled={tooltipsEnabled}
        />
      </div>
      <div className={styles.mainContent}>
        <StickyHeader
          day={worldState.day} currentTimeString={formatGameTime(worldState.currentTimeInMinutes, WAKE_UP_HOUR)}
          isTimeWarning={worldState.currentTimeInMinutes >= (MAX_DAY_MINUTES - 60) && !worldState._passedOut && !isProcessingEndOfDay}
          gold={playerData.gold} location={playerData.currentLocation}
          onRest={() => {
              if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
                  addMessage("Cannot rest now.", "warning"); return;
              }
              if (playerData.currentLocation === 'home') {
                handleRestDay(false);
              } else {
                addMessage("You must be at home to rest.", "warning");
              }
          }}
          onGoHome={() => {
              if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
                  addMessage("Cannot go home now.", "warning"); return;
              }
              // Allow going home during farming - will exit farming mini-game
              if (isFarmingMinigameActive) {
                  setIsFarmingMinigameActive(false);
                  addMessage("You leave your farm and head home.", "info");
              }
              const key = playerData.currentLocation === 'wilderness' ? 'returnHome' :
                          (playerData.currentLocation === 'shop' ? 'leaveShop' : null);
              if (key) goToLocation('home', key);
          }}
          onTravelTo={(location, actionKey) => {
              if (isCombatActive || isFishingMinigameActive || isMiningMinigameActive || isForagingMinigameActive || isWoodChoppingMinigameActive || isDungeonMiningMinigameActive || isBossWaveActive || showEndOfDayModal || worldState._passedOut || isProcessingEndOfDay) {
                  addMessage("Cannot travel now.", "warning"); return;
              }
              // Allow traveling during farming - will exit farming mini-game
              if (isFarmingMinigameActive) {
                  setIsFarmingMinigameActive(false);
                  addMessage("You leave your farm to travel.", "info");
              }
              goToLocation(location, actionKey);
          }}
          onTogglePause={handleTogglePause}
          canRest={playerData.currentLocation === 'home' && !worldState.isPaused && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isBossWaveActive && !showEndOfDayModal && !worldState._passedOut && !isProcessingEndOfDay}
          canGoHome={playerData.currentLocation !== 'home' && !worldState.isPaused && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isBossWaveActive && !showEndOfDayModal && !worldState._passedOut && !isProcessingEndOfDay}
          canTravel={!worldState.isPaused && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isBossWaveActive && !showEndOfDayModal && !worldState._passedOut && !isProcessingEndOfDay}
          isPaused={worldState.isPaused}
          // Mobile menu props
          isMobileMenuVisible={isMobileMenuVisible}
          onToggleMobileMenu={toggleMobileMenu}
          // Settings props
          tooltipsEnabled={tooltipsEnabled}
        />
         <div className={styles.panels}>
          <div className={styles.primaryPanel}>{renderMainPanels()}</div>
          <MainPanel2 />
        </div>
        
        {/* Collapsible Inventory Panel - Outside of main grid layout */}
        {(playerData.currentLocation === 'wilderness' || playerData.currentLocation === 'shop') && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isBossWaveActive && !showEndOfDayModal && (
          <CollapsibleInventoryPanel
              playerData={playerData}
              onEquipItem={handleEquipItem}
              onUseItem={handleUseItem}
              onSellItem={playerData.currentLocation === 'shop' ? handleSellItem_Robust : null}
              onToggleItemLock={handleToggleItemLock}
              isItemLocked={isItemLocked}
              onSellAllUnlockedItems={playerData.currentLocation === 'shop' ? handleSellAllUnlockedItems : null}
              shopContext={playerData.currentLocation === 'shop'}
          />
        )}
        
        {worldState.isPaused && !isCombatActive && !isFishingMinigameActive && !isMiningMinigameActive && !isForagingMinigameActive && !isWoodChoppingMinigameActive && !isDungeonMiningMinigameActive && !isFarmingMinigameActive && !isBossWaveActive && !showEndOfDayModal && (
          <div className={styles.pauseOverlay}>
            <div className={styles.pauseMessage}>
              <span className={styles.pauseIcon}>⏸️</span>
              <span>Game Paused</span>
              <span className={styles.pauseSubtext}>Choose an option to continue</span>
              
              {/* Resume Game Button */}
              <button
                className={`${styles.pauseMenuButton} ${styles.resumeButton}`}
                onClick={handleTogglePause}
                title="Resume the game"
              >
                <img src="/img/icons/play_button.png" alt="Resume" className={styles.pauseMenuButtonIcon} />
                <span>Resume Game</span>
              </button>
              
              {/* Quit to Dashboard Button - Only show for authenticated users */}
              {onReturnToDashboard && currentUser && !isGuestPlayer && (
                <button
                  className={styles.pauseMenuButton}
                  onClick={() => {
                    // Save before returning to dashboard (force immediate save)
                    saveGameData(true).then(() => {
                      console.log(`[Layout] Dashboard save complete for character ${playerData.name}.`);
                      onReturnToDashboard();
                    }).catch(error => {
                      console.error("[Layout] Error saving before dashboard return:", error);
                      onReturnToDashboard(); // Still navigate even if save fails
                    });
                  }}
                  title="Return to Character Dashboard"
                >
                  <img src="/img/icons/characterIcon.png" alt="Dashboard" className={styles.pauseMenuButtonIcon} />
                  <span>Quit to Dashboard</span>
                </button>
              )}
              
              {/* Settings Button */}
              <button
                className={styles.pauseMenuButton}
                onClick={openSettingsModal}
                title="Game Settings"
              >
                <img src="/img/icons/achievementIcon.png" alt="Settings" className={styles.pauseMenuButtonIcon} />
                <span>Settings</span>
              </button>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettingsModal && (
          <div className={styles.settingsOverlay}>
            <div className={styles.settingsModal}>
              <div className={styles.settingsHeader}>
                <h3>Game Settings</h3>
                <button 
                  className={styles.settingsCloseButton}
                  onClick={closeSettingsModal}
                  title="Close Settings"
                >
                  ✕
                </button>
              </div>
              
              <div className={styles.settingsContent}>
                <div className={styles.settingGroup}>
                  <h4>User Interface</h4>
                  
                  <div className={styles.settingItem}>
                    <label className={styles.settingLabel}>
                      <input
                        type="checkbox"
                        checked={tooltipsEnabled}
                        onChange={(e) => toggleTooltips(e.target.checked)}
                        className={styles.settingCheckbox}
                      />
                      <span className={styles.settingText}>Enable Tooltips</span>
                    </label>
                    <p className={styles.settingDescription}>
                      Show helpful tooltips when hovering over interface elements
                    </p>
                  </div>
                </div>
                
                <div className={styles.settingGroup}>
                  <h4>Developer Options</h4>
                  
                  <div className={styles.settingItem}>
                    <label className={styles.settingLabel}>
                      <input
                        type="checkbox"
                        checked={isAdminEnabled}
                        onChange={(e) => toggleAdminPanel(e.target.checked)}
                        className={styles.settingCheckbox}
                      />
                      <span className={styles.settingText}>Show Admin Panel</span>
                    </label>
                    <p className={styles.settingDescription}>
                      Display developer tools and admin controls (for testing)
                    </p>
                  </div>
                </div>
              </div>
              
              <div className={styles.settingsFooter}>
                <button 
                  className={styles.settingsCloseButton}
                  onClick={closeSettingsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showEndOfDayModal && (
        <EndOfDayModal
            dayNumber={endOfDayData.dayNumber}
            summary={endOfDayData.summary}
            onStartNextDay={handleStartNextDayFromModal}
            isPassingOut={endOfDayData.isPassingOut}
        />
      )}
      
      {/* Achievement Modal */}
      <AchievementModal
        achievement={currentAchievement}
        isVisible={showAchievementModal}
        onClose={handleAchievementModalClose}
      />
      
      {isCombatActive && currentCombatantPlayer && currentCombatantMonsters.length > 0 && (
        <CombatView
          player={currentCombatantPlayer}
          initialMonsters={currentCombatantMonsters}
          onCombatEnd={handleCombatEnd}
          isBossWaveFight={isBossWaveActive && bossEncounterQueue.length > 0 && bossEncounterQueue.length > 1 && !MonsterData[bossEncounterQueue[0]]?.isBoss}
          isFinalBossFight={isBossWaveActive && bossEncounterQueue.length === 1 && MonsterData[bossEncounterQueue[0]]?.isBoss}
          currentWave={currentWaveNumber}
          totalWaves={BOSS_WAVE_COUNT}
        />
      )}
      {isFishingMinigameActive && (
        <FishingMinigame
          playerFishingSkill={playerData.skills.fishing?.level || 0}
          playerData={playerData}
          onGameEnd={handleFishingEnd}
          onUseItem={handleUseItem}
          onUpdatePlayerData={setPlayerData}
        />
      )}
      {isMiningMinigameActive && (
        <MiningMinigame
          playerMiningSkill={playerData.skills.mining?.level || 0}
          onGameEnd={handleMiningEnd}
        />
      )}
      {isForagingMinigameActive && (
        <ForagingMinigame
          playerForagingSkill={playerData.skills.foraging?.level || 0}
          playerData={playerData}
          onGameEnd={handleForagingEnd}
        />
      )}
      {isWoodChoppingMinigameActive && (
        <WoodChoppingMinigame
          playerWoodcuttingSkill={playerData.skills.woodcutting?.level || 0}
          onGameEnd={handleWoodChoppingEnd}
        />
      )}
              {isFarmingMinigameActive && (
                  <FarmingMinigame
          playerData={playerData}
          onGameEnd={handleFarmingEnd}
          onEnergyUpdate={handleFarmingEnergyUpdate}
          worldState={worldState}
        />
        )}
      {isDungeonMiningMinigameActive && (
        <DungeonMiningMinigame
          playerData={playerData}
          onGameEnd={handleDungeonMiningEnd}
          onUpdatePlayerData={setPlayerData}
        />
      )}
      
      {/* Admin Panel - Only show if enabled */}
      {isAdminEnabled && (
        <CollapsibleAdminPanel
          playerData={playerData}
          onPlayerDataChange={setPlayerData}
          onAddMessage={addMessage}
        />
      )}
    </div>
  );
};

export default Layout;